import dayjs from 'dayjs'
import { type CSSProperties, type MouseEvent, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Paper,
  Popover,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import AddIcon from '@mui/icons-material/Add'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import PrintOutlinedIcon from '@mui/icons-material/PrintOutlined'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import VerifiedUserOutlinedIcon from '@mui/icons-material/VerifiedUserOutlined'
import CalendarTodayIcon from '@mui/icons-material/CalendarToday'
import CategoryIcon from '@mui/icons-material/Category'
import CompareArrowsIcon from '@mui/icons-material/CompareArrows'
import DownloadIcon from '@mui/icons-material/Download'
import AnalyticsIcon from '@mui/icons-material/Analytics'
import AssessmentIcon from '@mui/icons-material/Assessment'
import BarChartIcon from '@mui/icons-material/BarChart'
import BatchPredictionIcon from '@mui/icons-material/BatchPrediction'
import DashboardIcon from '@mui/icons-material/Dashboard'
import DonutLargeIcon from '@mui/icons-material/DonutLarge'
import InsightsIcon from '@mui/icons-material/Insights'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import PaidIcon from '@mui/icons-material/Paid'
import PercentIcon from '@mui/icons-material/Percent'
import RuleIcon from '@mui/icons-material/Rule'
import ScaleIcon from '@mui/icons-material/Scale'
import MonitorWeightIcon from '@mui/icons-material/MonitorWeight'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import TaskAltIcon from '@mui/icons-material/TaskAlt'
import type { GridColDef } from '@mui/x-data-grid'

import { useAppDispatch, useAppSelector } from '@/store'
import { API_BASE_URL } from '@/config/api'
import BrandState from '@/components/BrandState'
import ConfirmDialog from '@/components/ConfirmDialog'
import DateTextField from '@/components/DateTextField'
import {
  FuturesReportTable,
  displayAccessInteger,
  type ColumnDef as FuturesReportColumnDef,
  type ReportRow as FuturesReportRow,
} from './../futures/FuturesReportTable'
import { RPT } from '../dashboard/cashflows/reportTokens'
import PageHeader from '@/components/PageHeader'
import ReloadButton from '@/components/ReloadButton'
import ResizeHandle from '@/components/ResizeHandle'
import { useColumnWidths } from '@/hooks/useColumnWidths'
import { usePermission } from '@/hooks/usePermission'
import { useIsMobile } from '@/hooks/useResponsive'
import { useSnackbar } from '@/hooks/useSnackbar'
import {
  CARD_CONTENT_COMPACT,
  CARD_GRID_4UP,
  FILTER_HEADER_INPUT_SX,
  HIDE_ON_MOBILE_INLINE,
  HIDE_ON_TABLET_INLINE,
  TABLE_PAGINATION_COMPACT,
} from '@/theme/responsive'
import { sumNumericColumn } from '@/utils/tableTotals'
import {
  getTableSortSuffix,
  sortTableRows,
  type TableSortState,
  toggleTableSort,
} from '@/utils/tableSort'
import {
  factoryApi,
  useGetFactoryContextQuery,
  useGetFactorySummaryQuery,
  useGetFactoryTabRowsQuery,
  useGetFactoryNavigationPrefsQuery,
  usePatchFactoryNavigationPrefsMutation,
  useGetFactoryColumnsPrefsQuery,
  usePatchFactoryColumnsPrefsMutation,
  useSaveFactoryDateRangeMutation,
  useCreateFactoryRowMutation,
  useUpdateFactoryRowMutation,
  useDeleteFactoryRowMutation,
  useCopyFactoryReceiveMutation,
  useCopyFactoryDeliveryMutation,
  useCopyFactoryConsumablesMutation,
  useLazyGetReceiveQualityQuery,
  useSaveReceiveQualityMutation,
  useLazyGetDeliveryQualityQuery,
  useSaveDeliveryQualityMutation,
  type FactoryCell,
  type FactoryDateRange,
  type FactoryBRowPayload,
} from '@/store/api/factoryApi'
import { factoryColumnStorageKey } from './baoLoc/FactoryColumnPicker'
import { FuturesColumnsMenu } from './../futures/FuturesColumnsMenu'
import { usePreferenceColumns } from '@/utils/datasheetColumns/usePreferenceColumns'
import {
  type ColumnFilterAnchorPosition,
  type ColumnFilterKind,
  ColumnFilterIcon,
  ColumnFilterPopover,
  FilterBar,
  FilterButton,
  filterBarBtnSx as filterBarBtnSxTemplate,
  FilterPopover,
  SearchFilterContent,
  distinctColumnValues,
  inferColumnFilterKind,
  rowMatchesAllColumnFilters,
  rowMatchesSearch,
  useColumnFilters,
  useFilterPopover,
} from '@/components/dataList'
import { ConsumablesFormDialog } from './baoLoc/ConsumablesFormDialog'
import { DeliveryFormDialog } from './baoLoc/DeliveryFormDialog'
import FactoryItemListView from './baoLoc/FactoryItemListView'
import DeliveryPrintDialog from './baoLoc/DeliveryPrintDialog'
import DeliveryQualityDialog, {
  type DeliveryQualityDialogMode,
} from './baoLoc/DeliveryQualityDialog'
import FactoryReceiveQualityDialog, {
  type QualityDialogMode,
} from './baoLoc/FactoryReceiveQualityDialog'
import { InternalMovingFormDialog } from './baoLoc/InternalMovingFormDialog'
import { IssueFormDialog } from './baoLoc/IssueFormDialog'
import { LotAllocationFormDialog } from './baoLoc/LotAllocationFormDialog'
import { LotAllocationMassUploadDialog } from './baoLoc/LotAllocationMassUploadDialog'
import { LotMgtFormDialog } from './baoLoc/LotMgtFormDialog'
import { PoListFormDialog } from './baoLoc/PoListFormDialog'
import PoFinishedDialog from './baoLoc/PoFinishedDialog'
import { ReceiveFormDialog } from './baoLoc/ReceiveFormDialog'
import ReceivePrintDialog from './baoLoc/ReceivePrintDialog'
import { StackFormDialog } from './baoLoc/StackFormDialog'
import BatchReportPage from './batchReport/BatchReportPage'
import FactoryBomPage from './bom/FactoryBomPage'
import FactoryCostPage from './cost/FactoryCostPage'
import FactoryDeductionRatePage from './deductionRate/FactoryDeductionRatePage'
import FactoryQualityStandardPage from './qualityStandard/FactoryQualityStandardPage'
import ComingSoonPage from '../ComingSoonPage'
import { isFactoryTabBuilt, getFactoryTabUnbuild } from '../../config/pageBuildStatus'
import PContractTab from '../matching/contractOverview/PContractTab'
import SContractTab from '../matching/contractOverview/SContractTab'
import { type FactorySummaryMapCell } from './baoLoc/FactoryBaoLocSummaryMap'
import { FactorySummaryMap } from './maps/FactorySummaryMap'
import { useReportZoom } from '../dashboard/_shared/useReportZoom'
import { BlockTitle } from './maps/BlockTitle'
import {
  DEFAULT_FACTORY_TAB,
  FACTORY_TABS,
  isFactoryTabKey,
  type FactoryAction,
  type FactoryColumnConfig,
  type FactoryTabConfig,
} from './baoLoc/tabs'

type FactoryRow = { id: string | number } & Record<string, FactoryCell>

/** Tabs parked behind ComingSoonPage until rebuild lands. View workspace + list
 *  query đều skip cho các key này. Restore: remove key khỏi set.
 *  Bao gồm:
 *   - 1 list tab còn parked (`delivery`).
 *     `delivery` re-parked 2026-05-27 theo yêu cầu chị Đẹp để ưu tiên build
 *     Production trước (column/aggregate đã khớp Access spec, restore chỉ cần gỡ
 *     key khỏi set). `po-list` restored 2026-05-27 (mọi factory).
 *     `lot-mgt` restored 2026-05-27 (LotMgtFormDialog New/Edit/Copy — Copy mở
 *     form prefill từ dòng focus + Suggest-No lot_id, bám Access btnCopy_Click).
 *     `lot-allocation` restored 2026-05-31 (LotAllocationFormDialog New/Edit +
 *     Pending Bag/Kg snapshot giữ nguyên).
 *   - 4 hidden tab là nav target của data-hub cards (`consumables`, `movements`,
 *     `quality-standard`, `deduction-rate`) — chỉ
 *     accessible qua `/factory/<code>/data` hub, parking giúp toàn bộ non-BOM
 *     cards (cả select lẫn nav) đáp xuống ComingSoonPage. */
// Mã factory hợp lệ — KHỚP route literal trong App.tsx (`baoloc`, `nhontrach`…).
// App.tsx KHÔNG còn param `:factoryCode` (route literal để chặn typo catch-all),
// nên FactoryPage phải derive factoryCode từ pathname segment[0], không dùng
// useParams (sẽ rỗng → luôn fallback 'baoloc' → mọi factory load nhầm Bảo Lộc).
const FACTORY_CODES: ReadonlySet<string> = new Set([
  'baoloc',
  'nhontrach',
  'nguyencuong',
  'nongsan',
  'pacorini',
])

// Trạng thái "trỏ Page" cho tab factory tập trung ở `config/pageBuildStatus.ts`
// (FACTORY_TAB_UNBUILD). Mọi tab trừ `summary` đang Coming Soon — bật/tắt bằng
// cách sửa danh sách đó, KHÔNG remove code tab gốc ở đây.

/** Token Access huyền thoại — khớp với `api_user_token` trên backend (giống Futures / Purchase / Sale). */
const FACTORY_EXPORT_TOKEN = 'JDHK-NDEB-DBCK-DGEJ'

/** factoryCode (URL) → dataset code của endpoint export legacy
 *  (`/erp-legacy/download/factory[_filter]/data_<code>`). Mỗi nhà máy 1 dataset. */
const FACTORY_EXPORT_DATASET: Record<string, string> = {
  baoloc: 'data_bl',
  nhontrach: 'data_nt',
  nguyencuong: 'data_nc',
  pacorini: 'data_pacorini',
  nongsan: 'data_ns',
}

/** Freeze (sticky-left) cột định danh theo tab. Skill `/table-style` §7.6.
 *  - Receive: `grn_id`
 *  - Issue: `stack_id` + `po_no` (2 cột — dùng class `app-sticky-col-2` cho cột 2)
 *  - PO List: `po_no`
 *  - Lot Allocation: `lot_id`
 *  - Lot Management: `lot_id`
 *  - Stack: `stack_id`
 *  Các tab khác (delivery, consumables, movements, scale-data, …) chưa
 *  freeze — thêm key vào map dưới đây khi cần. */
const FROZEN_KEYS_BY_TAB: Record<string, ReadonlySet<string>> = {
  stack: new Set(['stack_id']),
  receive: new Set(['grn_id']),
  issue: new Set(['stack_id']),
  'po-list': new Set(['po_no']),
  'lot-allocation': new Set(['lot_id']),
  'lot-mgt': new Set(['lot_id']),
}
// Width default cột freeze — giảm nhẹ để không chiếm quá nhiều màn hình. Cells
// đã wrap autoheight nên cột hẹp hơn vẫn không cắt chữ (xuống dòng nếu dài).
const FROZEN_FALLBACK_WIDTH_DESKTOP_BY_TAB: Record<string, Record<string, number>> = {
  stack: { stack_id: 128 },
  receive: { grn_id: 150 },
  issue: { stack_id: 128 },
  'po-list': { po_no: 138 },
  'lot-allocation': { lot_id: 128 },
  'lot-mgt': { lot_id: 128 },
}
const FROZEN_FALLBACK_WIDTH_MOBILE_BY_TAB: Record<string, Record<string, number>> = {
  stack: { stack_id: 112 },
  receive: { grn_id: 132 },
  issue: { stack_id: 112 },
  'po-list': { po_no: 120 },
  'lot-allocation': { lot_id: 112 },
  'lot-mgt': { lot_id: 112 },
}

const getDownloadFilename = (disposition: string | null): string | null => {
  if (!disposition) return null

  const filenameStar = /filename\*\s*=\s*([^;]+)/i.exec(disposition)
  if (filenameStar?.[1]) {
    const value = filenameStar[1].trim().replace(/^UTF-8''/i, '').replace(/^"|"$/g, '')
    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }

  const filename = /filename\s*=\s*([^;]+)/i.exec(disposition)
  if (!filename?.[1]) return null
  return filename[1].trim().replace(/^"|"$/g, '')
}

const CRUD_ENDPOINT_TABS = new Set([
  'stack',
  'receive',
  'issue',
  'po-list',
  'delivery',
  'consumables',
  'lot-allocation',
  'lot-mgt',
  'quality-standard',
  'deduction-rate',
])

const DATA_CRUD_DATASETS = new Set([
  'item-list',
  'bom',
  'category-material',
  'movement-holidays',
  'movement-kpis',
])

// Phase 1 (2026-06-05): chỉ Internal Moving có dialog CRUD (row form). Input
// Daily/Monthly là ma trận KPI Goal&Real (editor riêng) — list-only tới khi
// build editor; thêm lại 'input-daily'/'input-monthly' khi xong. BE
// workspace_has_crud vẫn hỗ trợ cả 3 (không đổi), đây chỉ gate UI CRUD.
const MOVEMENTS_CRUD_DATASETS = new Set(['internal-moving'])

function workspaceDatasetForCrud(tab: FactoryTabConfig, datasetVal: string | undefined): string | undefined {
  if (!datasetVal) return undefined
  if (tab.endpoint === 'data' && DATA_CRUD_DATASETS.has(datasetVal)) return datasetVal
  if (tab.endpoint === 'movements' && MOVEMENTS_CRUD_DATASETS.has(datasetVal)) return datasetVal
  return undefined
}

function isCrudEnabled(tab: FactoryTabConfig, datasetVal: string | undefined): boolean {
  if (tab.kind === 'summary') return false
  if (tab.endpoint === 'data' || tab.endpoint === 'movements') return workspaceDatasetForCrud(tab, datasetVal) !== undefined
  return tab.kind === 'list' && CRUD_ENDPOINT_TABS.has(tab.endpoint)
}

function resolveDatasetForTab(tab: FactoryTabConfig, selectorState: Record<string, string>): string | undefined {
  const v = tab.selectors ? selectorState[tab.key] ?? tab.selectors[0]?.value : undefined
  return workspaceDatasetForCrud(tab, v)
}

/** Access btnPrintRp_Click pre-checks (form_frmFactoryReceiveSubForm.txt).
 *  Spec gốc chỉ có 2 case:
 *    1. `UCase(suppliers) = "PROCESSING"` → "No print for 'PROCESSING'".
 *    2. `DCount(view_factory_receive_print, "id = X and factory_code = ...") = 0`
 *       → "Please check Quality Deduction (click 'Quality' button to check)!"
 *  FE không gọi view trực tiếp ở client, nên dùng proxy: `pr_deduction_per`
 *  và `pr_deduction_kg` (cả 2 có trong grid view5) — đều 0/null = row trong
 *  view bị null id → tương đương DCount = 0. Tách rõ Deduction = 0 vào
 *  message để user biết phải sửa gì (Access message gốc generic, user complain
 *  không hiểu).
 *  `extra` cho phép override field bằng payload vừa save — view5 grid row
 *  chưa kịp refetch ngay sau Save. */
function receivePrintPrecheck(
  row: Record<string, FactoryCell> | null | undefined,
  extra?: Record<string, FactoryCell | undefined>,
): string | null {
  const pick = (key: string): string => {
    const v = extra?.[key] !== undefined ? extra[key] : row?.[key]
    return String(v ?? '').trim()
  }
  const pickNum = (key: string): number => {
    const s = pick(key)
    if (!s) return 0
    const n = Number(s)
    return Number.isFinite(n) ? n : 0
  }
  if (pick('suppliers').toUpperCase() === 'PROCESSING') {
    return "No print for 'PROCESSING'!"
  }
  if (pickNum('pr_deduction_per') === 0 && pickNum('pr_deduction_kg') === 0) {
    return "Quality Deduction = 0 for this GRN. Click 'Quality' to enter deduction values before printing."
  }
  return null
}

function getWorkspacePkPayload(
  ws: 'data' | 'movements',
  dataset: string,
  row: FactoryRow,
): FactoryBRowPayload {
  if (ws === 'data') {
    if (dataset === 'item-list' && row.code != null) return { code: row.code }
    if (dataset === 'bom' && row.factory_bom_id != null) return { factory_bom_id: row.factory_bom_id }
    if (dataset === 'category-material' && row.id != null) return { id: row.id }
    if (dataset === 'movement-holidays' && row.id != null) return { id: row.id }
    if (dataset === 'movement-kpis' && row.id != null) return { id: row.id }
  } else if (row.id != null) {
    return { id: row.id }
  }
  return {}
}

function getPkPayload(tab: FactoryTabConfig, row: FactoryRow, dataset?: string): FactoryBRowPayload {
  const ep = tab.endpoint
  if (ep === 'data' || ep === 'movements') {
    if (!dataset) return {}
    return getWorkspacePkPayload(ep, dataset, row)
  }
  if (ep === 'quality-standard' && row.factory_code != null && row.parameter_code != null && row.quality_code != null) {
    return {
      factory_code: row.factory_code,
      parameter_code: row.parameter_code,
      quality_code: row.quality_code,
    }
  }
  if (
    ep === 'deduction-rate' &&
    row.factory_code != null &&
    row.parameter_code != null &&
    row.field_value != null &&
    row.quality_code != null
  ) {
    return {
      factory_code: row.factory_code,
      parameter_code: row.parameter_code,
      field_value: row.field_value,
      quality_code: row.quality_code,
    }
  }
  if (ep === 'stack' && row.stack_id != null) return { stack_id: row.stack_id }
  if (ep === 'receive' && row.grn_id != null) return { grn_id: row.grn_id }
  if (ep === 'issue' && row.id != null) return { id: row.id }
  if (ep === 'po-list' && row.po_no != null) return { po_no: row.po_no }
  if (ep === 'delivery' && row.id != null) return { id: row.id }
  if (ep === 'consumables' && row.mrn_mdn_no != null) return { mrn_mdn_no: row.mrn_mdn_no }
  if (ep === 'lot-allocation' && row.id != null) return { id: row.id }
  if (ep === 'lot-mgt' && row.lot_id != null) return { lot_id: row.lot_id }
  return {}
}

function stringifyCell(v: FactoryCell): string {
  if (v == null) return ''
  return typeof v === 'string' ? v : String(v)
}

/* ------------------------------------------------------------------ */
/*  Filter bar + datasheet style helpers — copy từ SaleContract        */
/* ------------------------------------------------------------------ */

const ALL_ROWS_PAGE_SIZE = 1_000_000

/** Chip filter trước đây render Client/Type/Status/... 2026-05-26 đã thay
 *  bằng per-column filter ▽ trong header (skill /table-style §1). */

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => {
  if (v == null || v === '') return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`}`
}
const formatAccessDateTime = (v: unknown): string => {
  if (v == null || v === '') return ''
  const d = new Date(String(v))
  if (Number.isNaN(d.getTime())) return String(v)
  const day = `${d.getDate()}`.padStart(2, '0')
  const month = ACCESS_MONTHS[d.getMonth()]
  const year = `${d.getFullYear()}`
  const hh = `${d.getHours()}`.padStart(2, '0')
  const mm = `${d.getMinutes()}`.padStart(2, '0')
  return `${day}-${month}-${year} ${hh}:${mm}`
}
const formatAccessNumber = (v: number): string =>
  Number.isInteger(v)
    ? v.toLocaleString('en-US')
    : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

function formatCell(v: FactoryCell, type?: 'string' | 'number' | 'date' | 'datetime'): string {
  if (v == null || v === '') return ''
  if (type === 'datetime') return formatAccessDateTime(v)
  if (type === 'date') return formatAccessDate(v)
  if (type === 'number' && typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'number') return formatAccessNumber(v)
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

function draftToPayload(draft: Record<string, string>): FactoryBRowPayload {
  const out: FactoryBRowPayload = {}
  for (const [k, raw] of Object.entries(draft)) {
    if (raw.trim() === '') {
      out[k] = null
      continue
    }
    const asNum = Number(raw)
    if (!Number.isNaN(asNum) && String(asNum) === raw.trim()) {
      out[k] = asNum
    } else {
      out[k] = raw
    }
  }
  return out
}

/** Bộ nhãn cột Quality table — bám Access frmFactorySummarySubForm2
 *  (Label462..473 + lb2/lb3/lb4/lb5).
 *  Field name lowercase → header + group.
 *  4 stage × 2 metric pair (TL TỊNH / QUY CHUẨN) khớp 8 cột số của view
 *  `tlc_factory_bl.view_factory_summary2`. */
// Group label dùng `\n` để FuturesReportTable render thành 2 dòng VN + EN (khớp
// Access lb2/lb3/lb4/lb5 caption `\015\012` line break).
const QUALITY_COLUMN_META: Record<string, { headerName: string; group?: string }> = {
  quality_code: { headerName: 'Quality' },
  description: { headerName: 'Spec' },
  txt_net_weight: { headerName: 'Net weight', group: 'Coffee receipt' },
  txt_base_weight: { headerName: 'Basis weight', group: 'Coffee receipt' },
  txt_net_weight2: { headerName: 'Net weight', group: 'Issue to production' },
  txt_base_weight2: { headerName: 'Basis weight', group: 'Issue to production' },
  txt_net_weight3: { headerName: 'Net weight', group: 'OUT OF FACTORY' },
  txt_exnet_weight: { headerName: 'Basis weight', group: 'OUT OF FACTORY' },
  txt_exnet_weight2: { headerName: 'Net weight', group: 'Inventory' },
  txtex_base_weight: { headerName: 'Basis weight', group: 'Inventory' },
}

/** Bộ nhãn cột Consumables / Category — Access Label614..619 (TÊN VẬT TƯ /
 *  NHẬP / XUẤT KHỎI NHÀ MÁY / TIÊU HAO HƯ HỎNG / XUẤT CHO SẢN XUẤT / TỒN).
 *  Field từ view `view_factory_summary3`: category, col1..col5. */
const CATEGORY_COLUMN_META: Record<string, { headerName: string }> = {
  category: { headerName: 'Category' },
  col1: { headerName: 'In' },
  col2: { headerName: 'Out' },
  col3: { headerName: 'Consumed' },
  col4: { headerName: 'Using' },
  col5: { headerName: 'Balance' },
}

/** Nhãn cột / trường (Access: txt_* → đọc được). */
function humanizeFieldKey(key: string) {
  return key
    .replace(/^txt_/i, '')
    .replace(/_/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase())
}

/** Giá trị một ô số liệu trên dashboard Summary (read-only). */
function formatSummaryMetricValue(v: FactoryCell): string {
  if (v === null || v === undefined || v === '') return '—'
  if (typeof v === 'number') return Number.isFinite(v) ? v.toLocaleString('en-US') : '—'
  if (typeof v === 'boolean') return v ? 'Yes' : 'No'
  return String(v)
}

/** Giá trị phân bổ tồn kho theo khu — đúng các cột Access frmFactorySummarySubForm2 (view_factory_summary_sum). */
const SUMMARY_ALLOCATION_ROWS: Array<{ labelVi: string; valueNormKey: string }> = [
  { labelVi: 'Total (stack + WIP)', valueNormKey: 'text996' },
  { labelVi: 'Zone A (BL.A.*)', valueNormKey: 'text1379' },
  { labelVi: 'Zone B (BL.B.*)', valueNormKey: 'text1381' },
  { labelVi: 'Zone C (BL.C.*)', valueNormKey: 'text999' },
  { labelVi: 'Unassigned area', valueNormKey: 'text1004' },
]

/** Các cột Text* dùng cho tiêu đề / phân bổ — không hiển thị trong lưới map. */
const SUMMARY_TEXT_KEYS_OUTSIDE_MAP = new Set(
  [
    'text952',
    'text953',
    'text954',
    'text955',
    'text956',
    'text995',
    'text996',
    'text1374',
    'text1379',
    'text1380',
    'text1381',
    'text1382',
    'text999',
    'text1000',
    'text1004',
    'text1005',
  ],
)

function getRowKeyCaseInsensitive(row: Record<string, FactoryCell>, normKey: string): string | undefined {
  const want = normKey.toLowerCase()
  return Object.keys(row).find((k) => k.toLowerCase() === want)
}

function isSummaryKpiFieldKey(key: string): boolean {
  const k = key.toLowerCase()
  return k.startsWith('txt_') || k.startsWith('txtex_') || k === 'kv_sx'
}

function extractSummaryMapCells(sumRow: Record<string, FactoryCell>): FactorySummaryMapCell[] {
  const cells: FactorySummaryMapCell[] = []
  const consumed = new Set<string>()
  const mark = (k: string) => consumed.add(k.toLowerCase())
  const used = (k: string) => consumed.has(k.toLowerCase())
  const keys = Object.keys(sumRow)

  for (const k of keys) {
    if (k.toLowerCase() === 'id') mark(k)
    else if (isSummaryKpiFieldKey(k)) mark(k)
    else if (SUMMARY_TEXT_KEYS_OUTSIDE_MAP.has(k.toLowerCase())) mark(k)
  }

  for (const k of keys) {
    if (used(k)) continue
    const kl = k.toLowerCase()
    if (!kl.endsWith('_v')) continue
    const base = k.slice(0, -2)
    const nk = keys.find((x) => x.toLowerCase() === `${base.toLowerCase()}_n`)
    if (!nk || used(nk)) continue
    const label = stringifyCell(sumRow[nk])
    cells.push({ field: k, label: label || base, value: sumRow[k] })
    mark(k)
    mark(nk)
  }

  const textNums = keys
    .map((k) => {
      const m = k.match(/^text(\d+)$/i)
      return m ? { k, n: parseInt(m[1], 10) } : null
    })
    .filter((x): x is { k: string; n: number } => x != null)
    .sort((a, b) => a.n - b.n)

  for (let i = 0; i < textNums.length - 1; i++) {
    const cur = textNums[i]
    const next = textNums[i + 1]
    if (next.n !== cur.n + 1) continue
    const k1 = cur.k
    const k2 = next.k
    if (used(k1) || used(k2)) continue
    const lk1 = k1.toLowerCase()
    const lk2 = k2.toLowerCase()
    if (SUMMARY_TEXT_KEYS_OUTSIDE_MAP.has(lk1) || SUMMARY_TEXT_KEYS_OUTSIDE_MAP.has(lk2)) continue

    const va = sumRow[k1]
    const vb = sumRow[k2]
    const numA = typeof va === 'number'
    const numB = typeof vb === 'number'
    if (numA === numB) continue
    const value = numA ? va : vb
    const labelRaw = numA ? vb : va
    if (labelRaw != null && typeof labelRaw !== 'string' && typeof labelRaw !== 'number') continue
    const labelStr =
      typeof labelRaw === 'number' && Number.isFinite(labelRaw)
        ? String(labelRaw)
        : stringifyCell(labelRaw as FactoryCell)
    cells.push({ field: numA ? k1 : k2, label: labelStr || k1, value })
    mark(k1)
    mark(k2)
  }

  return cells.sort((a, b) => a.label.localeCompare(b.label, 'vi'))
}

function partitionFactorySummaryRow(sumRow: Record<string, FactoryCell> | null | undefined): {
  kpiEntries: Array<[string, FactoryCell]>
  allocationRows: Array<{ labelVi: string; value: FactoryCell; field: string }>
  mapCells: FactorySummaryMapCell[]
  sectionNote?: FactoryCell
} {
  if (!sumRow) {
    return { kpiEntries: [], allocationRows: [], mapCells: [] }
  }
  const kpiEntries = Object.entries(sumRow).filter(([k]) => k.toLowerCase() !== 'id' && isSummaryKpiFieldKey(k))

  const allocationRows: Array<{ labelVi: string; value: FactoryCell; field: string }> = []
  for (const spec of SUMMARY_ALLOCATION_ROWS) {
    const orig = getRowKeyCaseInsensitive(sumRow, spec.valueNormKey)
    if (!orig) continue
    allocationRows.push({ labelVi: spec.labelVi, value: sumRow[orig], field: orig })
  }

  const mapCells = extractSummaryMapCells(sumRow)
  const noteKey = getRowKeyCaseInsensitive(sumRow, 'text995')
  const sectionNote = noteKey ? sumRow[noteKey] : undefined

  return {
    kpiEntries,
    allocationRows,
    mapCells,
    sectionNote,
  }
}

/**
 * Resolve column configs — 1:1 với `tab.columns` khai báo. Không tự append
 * field thừa từ API (chỉ user explicit qua tab.columns).
 *
 * Khi tab.columns rỗng (chưa khai báo schema) → fallback dùng row[0] keys
 * (cho tab dataset / cũ chưa migrate).
 */
function resolveListColumnConfigs(
  tab: FactoryTabConfig,
  rows: FactoryRow[],
  dataset?: string,
): FactoryColumnConfig[] {
  // Selector tab có nhiều dataset shape khác nhau (movements) → ưu tiên
  // datasetColumns[dataset] (mỗi dataset 1 bộ cột bám Access ColumnOrder).
  const perDataset = dataset ? tab.datasetColumns?.[dataset] : undefined
  if (perDataset && perDataset.length > 0) return perDataset

  // Tab đã khai báo schema → render đúng list, không suy đoán cột thêm.
  if (tab.columns.length > 0) return tab.columns

  // Fallback cho tab chưa có schema (tab.columns rỗng): suy từ row[0].
  if (!rows.length) return tab.columns
  const sample = rows[0]
  return Object.keys(sample)
    .filter((k) => k !== 'id')
    .map((field) => {
      const v = sample[field]
      const inferredNum = typeof v === 'number'
      return {
        field,
        headerName: humanizeFieldKey(field),
        flex: 1,
        align: inferredNum ? ('right' as const) : ('left' as const),
        type: inferredNum ? ('number' as const) : ('string' as const),
      }
    })
}

type PendingFormAction = 'new' | 'edit' | 'quality' | 'delete' | 'copy'

interface PendingAction {
  action: PendingFormAction
  row: FactoryRow | null
  tab: FactoryTabConfig
}

const headerButtonSx = {
  minHeight: 26,
  minWidth: { xs: 0, sm: 'auto' },
  px: { xs: 0.4, sm: 0.75 },
  py: 0.2,
  fontSize: { xs: '0.62rem', sm: '0.68rem' },
  lineHeight: 1.1,
  textTransform: 'none' as const,
  '& .MuiButton-startIcon': {
    mr: { xs: 0.25, sm: 1 },
    ml: { xs: 0, sm: -0.25 },
  },
}

function getDefaultDateRange(): FactoryDateRange {
  const now = dayjs()
  const fiscalYearStart = now.month() >= 9 ? dayjs(`${now.year()}-10-01`) : dayjs(`${now.year() - 1}-10-01`)
  const fiscalYearEnd = now.month() >= 9 ? dayjs(`${now.year() + 1}-09-30`) : dayjs(`${now.year()}-09-30`)

  return {
    startDate: fiscalYearStart.format('YYYY-MM-DD'),
    endDate: fiscalYearEnd.format('YYYY-MM-DD'),
  }
}


function normalizeRows(
  apiRows: Array<Record<string, FactoryCell>> | undefined,
  sampleRows: FactoryTabConfig['sampleRows'],
): FactoryRow[] {
  const sourceRows = apiRows && apiRows.length ? apiRows : sampleRows
  return sourceRows.map((row, index) => {
    const rawId =
      row.id ??
      row.stack_id ??
      row.grn_id ??
      row.gdn_id ??
      row.lot_id ??
      row.po_no ??
      row.match_id ??
      row.mrn_mdn_no ??
      row.issue_no ??
      index + 1
    return { id: rawId as string | number, ...row } as FactoryRow
  })
}

function buildGridColumns(columns: FactoryColumnConfig[]): GridColDef<FactoryRow>[] {
  return columns.map((column) => ({
    field: column.field,
    headerName: column.headerName,
    width: column.width,
    flex: column.flex,
    type: column.type === 'number' ? 'number' : 'string',
    align: column.align,
    headerAlign: column.align,
    valueFormatter:
      column.type === 'number'
        ? (value: unknown) => (typeof value === 'number' ? value.toLocaleString('en-US') : value)
        : column.type === 'datetime'
          ? (value: unknown) => formatAccessDateTime(value)
          : column.type === 'date'
            ? (value: unknown) => formatAccessDate(value)
            : undefined,
  }))
}

/** PO List: cờ Finished của 1 row (view trả boolean; phòng hờ 1/'1'/'Yes'). */
function isRowFinished(row: FactoryRow): boolean {
  const v = row.is_finished
  return (
    v === true ||
    v === 1 ||
    v === '1' ||
    (typeof v === 'string' && v.trim().toLowerCase() === 'yes')
  )
}

function getActionLabel(action: FactoryAction) {
  const labels: Record<FactoryAction, string> = {
    new: 'New',
    edit: 'Edit',
    delete: 'Delete',
    copy: 'Copy',
    quality: 'Quality',
    print: 'Print',
    'mass-upload': 'Mass upload',
    finished: 'Set Finished',
  }
  return labels[action]
}

function getActionIcon(action: FactoryAction): ReactNode {
  switch (action) {
    case 'new':
      return <AddIcon sx={{ fontSize: 16 }} />
    case 'edit':
      return <EditOutlinedIcon sx={{ fontSize: 16 }} />
    case 'delete':
      return <DeleteOutlineIcon sx={{ fontSize: 16 }} />
    case 'copy':
      return <ContentCopyIcon sx={{ fontSize: 16 }} />
    case 'quality':
      return <VerifiedUserOutlinedIcon sx={{ fontSize: 16 }} />
    case 'print':
      return <PrintOutlinedIcon sx={{ fontSize: 16 }} />
    case 'mass-upload':
      return <UploadFileIcon sx={{ fontSize: 16 }} />
    case 'finished':
      return <TaskAltIcon sx={{ fontSize: 16 }} />
    default:
      return null
  }
}

function FactoryHeader({
  title,
  storeChar,
  filters,
  invalidRange,
  exporting,
  email,
  reloadBusy,
  onFilterChange,
  onExport,
  onReload,
}: {
  title: string
  storeChar: string
  filters: FactoryDateRange
  invalidRange: boolean
  exporting: 'all' | 'filter' | null
  email: string
  reloadBusy: boolean
  onFilterChange: (next: FactoryDateRange) => void
  onExport: (filtered: boolean) => void
  onReload?: () => void
}) {
  return (
    <PageHeader
      title={title}
      module="Factory"
      actions={
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          spacing={{ xs: 0.55, lg: 0.75 }}
          alignItems={{ xs: 'stretch', lg: 'center' }}
          justifyContent="space-between"
          sx={{ width: '100%' }}
        >
          <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
            <Typography
              variant="h6"
              sx={{
                color: 'primary.dark',
                fontWeight: 900,
                lineHeight: 1.15,
                textTransform: 'uppercase',
                fontSize: { xs: '0.92rem', sm: '1rem' },
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            <Chip
              size="small"
              label={storeChar}
              sx={{
                height: 22,
                borderRadius: 999,
                bgcolor: 'rgba(244, 223, 156, 0.34)',
                color: 'secondary.dark',
                border: '1px solid rgba(218, 131, 60, 0.28)',
                fontWeight: 900,
                fontSize: '0.65rem',
              }}
            />
          </Stack>

          <Stack
            direction="row"
            spacing={{ xs: 0.25, sm: 0.45 }}
            alignItems="center"
            useFlexGap
            flexWrap={{ xs: 'nowrap', md: 'wrap' }}
            justifyContent={{ xs: 'flex-start', lg: 'flex-end' }}
            sx={{
              minWidth: 0,
              overflowX: { xs: 'auto', md: 'visible' },
              // Hide scrollbar visually but keep scroll on xs.
              scrollbarWidth: { xs: 'thin', md: 'auto' },
              '&::-webkit-scrollbar': { height: { xs: 3, md: 8 } },
            }}
          >
            <DateTextField
              label="Date"
              size="small"
              value={filters.startDate}
              error={invalidRange}
              onChange={(value) => onFilterChange({ ...filters, startDate: value || getDefaultDateRange().startDate })}
              sx={{ ...FILTER_HEADER_INPUT_SX, width: { xs: 96, sm: 158 }, flexShrink: 0 }}
              InputLabelProps={{ shrink: true }}
              nativeInputProps={{ 'aria-label': 'Factory date start' }}
            />
            <ArrowRightAltIcon
              fontSize="small"
              sx={{ color: 'text.secondary', fontSize: { xs: 14, sm: 18 }, mx: { xs: 0, sm: 0.25 }, flexShrink: 0 }}
              aria-hidden="true"
            />
            <DateTextField
              size="small"
              value={filters.endDate}
              error={invalidRange}
              onChange={(value) => onFilterChange({ ...filters, endDate: value || getDefaultDateRange().endDate })}
              sx={{ ...FILTER_HEADER_INPUT_SX, width: { xs: 96, sm: 158 }, flexShrink: 0 }}
              nativeInputProps={{ 'aria-label': 'Factory date end' }}
            />
            <ReloadButton
              sx={{ ...headerButtonSx, flexShrink: 0 }}
              apiTags={[]}
              baseTags={['Factory']}
              busy={reloadBusy}
              onClick={onReload}
            />
            <Button
              size="small"
              variant="outlined"
              sx={{ ...headerButtonSx, flexShrink: 0 }}
              disabled={exporting !== null || !email || invalidRange}
              onClick={() => onExport(true)}
              startIcon={
                exporting === 'filter' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
              title="Export (Filter)"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export (Filter)</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Filter</Box>
            </Button>
            <Button
              size="small"
              variant="outlined"
              sx={{ ...headerButtonSx, flexShrink: 0 }}
              disabled={exporting !== null || !email}
              onClick={() => onExport(false)}
              startIcon={
                exporting === 'all' ? (
                  <CircularProgress size={11} color="inherit" />
                ) : (
                  <DownloadIcon fontSize="small" />
                )
              }
              title="Export (All)"
            >
              <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Export (All)</Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>All</Box>
            </Button>
          </Stack>
        </Stack>
      }
    />
  )
}

function FactorySummary({
  factoryCode,
  factoryName,
  zoom = 1,
}: {
  factoryCode: string
  factoryName?: string
  /** CSS zoom điều khiển từ control trên block Tabs chung (lift state ở FactoryPage). */
  zoom?: number
}) {
  const { data, isFetching, isError } = useGetFactorySummaryQuery(factoryCode, {
    refetchOnMountOrArgChange: true,
  })

  // sectionNote vẫn lấy từ sumRow (Access metadata note); mapFromSumRow là
  // fallback parser khi BE chưa expose summary_map_stock. Allocation rows
  // KHÔNG còn compute ở đây — chuyển vào từng map component để mỗi factory
  // tự thống kê theo zone codes của nó.
  const { mapCells: mapFromSumRow, sectionNote } = useMemo(
    () => partitionFactorySummaryRow(data?.sumRow ?? null),
    [data?.sumRow],
  )

  const mapCells = useMemo(() => {
    const rows = data?.summaryMapStock
    if (rows?.length) {
      return rows.map((r) => ({
        field: r.fullAreaCode,
        label: r.areaCode?.trim() ? r.areaCode.trim() : r.fullAreaCode,
        value: r.qty as FactoryCell,
      }))
    }
    return mapFromSumRow
  }, [data?.summaryMapStock, mapFromSumRow])

  /** Build column configs (chứa type info) — dùng cho cả GridColDef và FuturesReportTable. */
  const qualityColConfigs = useMemo<FactoryColumnConfig[]>(() => {
    const sample = data?.byQuality?.[0]
    if (!sample) return []
    return Object.keys(sample)
      .filter((k) => k !== 'id')
      .map((field) => {
        const meta = QUALITY_COLUMN_META[field.toLowerCase()]
        return {
          field,
          headerName: meta?.headerName ?? humanizeFieldKey(field),
          flex: 1,
          align: typeof sample[field] === 'number' ? ('right' as const) : ('left' as const),
          type: typeof sample[field] === 'number' ? ('number' as const) : ('string' as const),
        }
      })
  }, [data?.byQuality])

  const qualityRows = useMemo(
    () =>
      normalizeRows(
        data?.byQuality,
        [],
      ),
    [data?.byQuality],
  )

  const categoryColConfigs = useMemo<FactoryColumnConfig[]>(() => {
    const sample = data?.byCategory?.[0]
    if (!sample) return []
    return Object.keys(sample)
      .filter((k) => k !== 'id')
      .map((field) => {
        const meta = CATEGORY_COLUMN_META[field.toLowerCase()]
        return {
          field,
          headerName: meta?.headerName ?? humanizeFieldKey(field),
          flex: 1,
          align: typeof sample[field] === 'number' ? ('right' as const) : ('left' as const),
          type: typeof sample[field] === 'number' ? ('number' as const) : ('string' as const),
        }
      })
  }, [data?.byCategory])

  const categoryRows = useMemo(
    () =>
      normalizeRows(
        data?.byCategory,
        [],
      ),
    [data?.byCategory],
  )

  /* ── Map sang FuturesReportTable: ColumnDef + numericFormatter Access-like.
   *    Summary view không có column picker — hiển thị tất cả cột.
   *    `group` đặt theo Access lb2..lb5 (TỔNG NHẬP / ĐƯA VÀO SX / TỔNG XUẤT / TỒN KHO). ── */
  const qualityReportColumns = useMemo<FuturesReportColumnDef[]>(
    () =>
      qualityColConfigs.map((c) => {
        const field = c.field.toLowerCase()
        const meta = QUALITY_COLUMN_META[field]
        // Title columns (frozen) cần đủ rộng để không cắt nội dung khi sticky.
        const minWidth =
          field === 'quality_code' ? 110 : field === 'description' ? 200 : 80
        return {
          key: c.field,
          label: c.headerName,
          group: meta?.group,
          numeric: c.type === 'number',
          minWidth,
        }
      }),
    [qualityColConfigs],
  )
  /** Hai dòng tổng pin xuống footer (khớp Access frmFactorySummarySubForm2):
   *    1. "Hàng SX dở dang · Work-in-progress" — Label475/476 — value = sumRow.txt_unfinished
   *    2. "TỔNG · Total"                        — Label486      — values = sumRow.txt_{col}sum
   *
   *  Cả 2 row đều có `fullSymbol='total'` để FuturesReportTable pinTotalRow render
   *  trong TableFooter với style gold. txt_*sum lookup theo convention:
   *    byQuality.<field>  ↔  sumRow.txt_<field>sum / txt_<field>2sum / txtex_<field>sum / ...
   *  Nếu sumRow không có giá trị tương ứng → fallback compute client-side sum. */
  const qualityReportRows = useMemo<FuturesReportRow[]>(() => {
    const base = qualityRows as FuturesReportRow[]
    if (!base.length || !qualityReportColumns.length) return base

    const sumRow = (data?.sumRow ?? null) as Record<string, FactoryCell> | null
    const sumLookup = new Map<string, FactoryCell>()
    if (sumRow) {
      for (const [k, v] of Object.entries(sumRow)) sumLookup.set(k.toLowerCase(), v)
    }
    const pickSum = (field: string): number | null => {
      // Thử các pattern: txt_<field>sum, txt_<field>_sum, txtex_<field>sum
      const candidates = [
        `txt_${field}sum`,
        `txt_${field}_sum`,
        `txtex_${field}sum`,
        `txtex_${field}_sum`,
      ]
      for (const c of candidates) {
        const v = sumLookup.get(c.toLowerCase())
        if (typeof v === 'number' && Number.isFinite(v)) return v
      }
      return null
    }
    const clientSum = (key: string): number | null => {
      let sum = 0
      let hasAny = false
      for (const r of base) {
        const v = r[key]
        if (typeof v === 'number' && Number.isFinite(v)) {
          sum += v
          hasAny = true
        }
      }
      return hasAny ? sum : null
    }

    // Access form: Label475/486 nằm tại Left ~ 1500-5478, đúng cột "description"
    // (QUY CÁCH). Không phải cột "quality_code" (CHẤT LƯỢNG, Left ~ 0). → label
    // của 2 dòng footer hiển thị ở cột description, cùng cấp như Access.
    const labelCol =
      qualityReportColumns.find((c) => c.key.toLowerCase() === 'description') ??
      qualityReportColumns.find((c) => !c.numeric)

    // Row 1: "Hàng SX dở dang · Work-in-progress"
    // Access form: txt_unfinished nằm Left=14460, cùng cột với txt_exnet_weight2sum
    // → đặt value vào cột TỒN KHO TL TỊNH (field `txt_exnet_weight2`). Fallback: first numeric col.
    // `fullSymbol: 'subtotal'` → FuturesReportTable render với class `subtotal` (lighter gold + italic).
    const wipRow: FuturesReportRow = { fullSymbol: 'subtotal' }
    if (labelCol) wipRow[labelCol.key] = 'WORK-IN-PROGRESS'
    const wipValue = sumLookup.get('txt_unfinished')
    if (typeof wipValue === 'number' && Number.isFinite(wipValue)) {
      const wipTargetCol =
        qualityReportColumns.find((c) => c.key.toLowerCase() === 'txt_exnet_weight2') ??
        qualityReportColumns.find((c) => c.numeric)
      if (wipTargetCol) wipRow[wipTargetCol.key] = wipValue
    }

    // Row 2: "TỔNG · Total"
    const totalRow: FuturesReportRow = { fullSymbol: 'total' }
    if (labelCol) totalRow[labelCol.key] = 'TOTAL'
    for (const col of qualityReportColumns) {
      if (!col.numeric) continue
      const fromSum = pickSum(col.key)
      totalRow[col.key] = fromSum != null ? fromSum : clientSum(col.key)
    }
    // Cột INVENTORY · Net weight (txt_exnet_weight2): Total cộng thêm
    // Work-in-progress (chị Đẹp 2026-06-24) — tồn kho gồm cả hàng SX dở dang.
    const invCol = qualityReportColumns.find((c) => c.key.toLowerCase() === 'txt_exnet_weight2')
    if (invCol && typeof wipValue === 'number' && Number.isFinite(wipValue)) {
      const cur = totalRow[invCol.key]
      totalRow[invCol.key] = (typeof cur === 'number' && Number.isFinite(cur) ? cur : 0) + wipValue
    }

    return [...base, wipRow, totalRow]
  }, [qualityRows, qualityReportColumns, data?.sumRow])

  const categoryReportColumns = useMemo<FuturesReportColumnDef[]>(
    () =>
      categoryColConfigs.map((c) => {
        const field = c.field.toLowerCase()
        const minWidth = field === 'category' ? 200 : 90
        return {
          key: c.field,
          label: c.headerName,
          numeric: c.type === 'number',
          minWidth,
        }
      }),
    [categoryColConfigs],
  )
  const categoryReportRows = useMemo<FuturesReportRow[]>(() => {
    const base = categoryRows as FuturesReportRow[]
    if (!base.length || !categoryReportColumns.length) return base
    const total: FuturesReportRow = { fullSymbol: 'total' }
    for (const col of categoryReportColumns) {
      if (col.numeric) {
        let sum = 0
        let hasAny = false
        for (const r of base) {
          const v = r[col.key]
          if (typeof v === 'number' && Number.isFinite(v)) {
            sum += v
            hasAny = true
          }
        }
        total[col.key] = hasAny ? sum : null
      }
    }
    const firstCol = categoryReportColumns[0]
    if (firstCol && !firstCol.numeric) total[firstCol.key] = 'TOTAL'
    return [...base, total]
  }, [categoryRows, categoryReportColumns])

  /* Layout 3 hàng (stacked full-width):
   *   Hàng 1: TỒN KHO THEO CHẤT LƯỢNG (Coffee stocks by qualities).
   *   Hàng 2: KHU VỰC VẬT TƯ (Consumables area) — full width.
   *   Hàng 3: SƠ ĐỒ KHO + PHÂN BỐ TỒN KHO (warehouse map + allocation).
   * Per request 2026-05-20: SƠ ĐỒ KHO chuyển xuống dòng 3, KHU VỰC VẬT TƯ full-width.
   */
  return (
    // overflowY:auto — Summary là vùng scroll nội bộ (header/tabs cố định). Chuỗi
    // app-shell bound 100vh (<main> overflow:auto, Card MUI overflow:hidden) nên
    // nội dung Summary cao hơn 1 màn PHẢI có container scroll riêng, không thì bị
    // Card cắt cụt. pr nhỏ chừa chỗ scrollbar. [[app-shell-scroll]]
    <Stack spacing={2} sx={{ flex: 1, minHeight: 0, overflowY: 'auto', pr: 0.5 }}>
      {isError ? (
        <Alert severity="error">
          Could not load summary data. Please try again or check the system connection.
        </Alert>
      ) : null}

      {/* Vùng nội dung report — áp CSS `zoom` (reflow đúng, không transform). */}
      <Stack component="div" spacing={2} sx={{ zoom }}>
      {/* ── Row 1: COFFEE STOCKS BY QUALITIES — full width ─────────── */}
      <ReportCard title="Coffee stocks by qualities">
        {isFetching && !qualityReportRows.length ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <FuturesReportTable
            columns={qualityReportColumns}
            rows={qualityReportRows}
            density="compact"
            pinTotalRow
            numericFormatter={displayAccessInteger}
            scrollMaxHeight={360}
            horizontalScroll
            freezeTitleColumns
            skin="report"
            cellFontSize={{ head: '0.66rem', body: '0.68rem' }}
          />
        )}
        {typeof sectionNote === 'string' && sectionNote.trim() ? (
          <Typography
            variant="caption"
            sx={{ display: 'block', fontStyle: 'italic', lineHeight: 1.3, mt: 0.75, color: RPT.sub }}
          >
            {sectionNote}
          </Typography>
        ) : null}
      </ReportCard>

      {/* ── Row 2: CONSUMABLES AREA — full width ──────────────────── */}
      <ReportCard title="Consumables area">
        {isFetching && !categoryReportRows.length ? (
          <Stack alignItems="center" justifyContent="center" sx={{ py: 4 }}>
            <CircularProgress size={24} />
          </Stack>
        ) : (
          <FuturesReportTable
            columns={categoryReportColumns}
            rows={categoryReportRows}
            density="compact"
            pinTotalRow
            numericFormatter={displayAccessInteger}
            scrollMaxHeight={360}
            horizontalScroll
            freezeTitleColumns
            skin="report"
            cellFontSize={{ head: '0.66rem', body: '0.68rem' }}
          />
        )}
      </ReportCard>

      {/* ── Row 3: SƠ ĐỒ KHO + PHÂN BỐ TỒN KHO — full width ──────── */}
      {!mapCells.length && data?.sumRow ? (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          No stock allocation data.
        </Typography>
      ) : null}
      <FactorySummaryMap
        factoryCode={factoryCode}
        factoryName={factoryName}
        mapCells={mapCells}
        sumRow={data?.sumRow ?? null}
        kvSx={data?.summaryWarehouseSplit?.kvSx ?? null}
        warehouseSplit={data?.summaryWarehouseSplit ?? null}
      />
      </Stack>
    </Stack>
  )
}

/** Card trắng tone dashboard Cashflows (erp-report-cash): viền mảnh radius 8,
 *  title ink đậm + caption mảnh bên phải — thay band SectionHeader cũ. */
function ReportCard({
  title,
  caption,
  children,
}: {
  title: string
  caption?: string
  children: React.ReactNode
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        p: 1,
        border: `1px solid ${RPT.border}`,
        borderRadius: '8px',
        bgcolor: '#fff',
        minWidth: 0,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 0.75 }}>
        <BlockTitle title={title} />
        <Box sx={{ flex: 1 }} />
        {caption ? (
          <Typography sx={{ fontSize: '0.66rem', color: RPT.gold, fontWeight: 600 }}>{caption}</Typography>
        ) : null}
      </Stack>
      {children}
    </Paper>
  )
}

/** Action button color scheme — match SaleContract ContractListView: new=success,
 *  copy=primary, edit=warning, delete=error, quality/print=primary.main border. */
function actionButtonProps(action: FactoryAction): {
  variant: 'contained' | 'outlined'
  color: 'success' | 'primary' | 'warning' | 'error' | 'inherit'
  sx?: object
} {
  switch (action) {
    case 'new':
      return { variant: 'contained', color: 'success' }
    case 'copy':
      return { variant: 'outlined', color: 'primary' }
    case 'edit':
      return { variant: 'outlined', color: 'warning' }
    case 'delete':
      return { variant: 'outlined', color: 'error' }
    case 'finished':
      return { variant: 'outlined', color: 'success' }
    case 'print':
    case 'quality':
    default:
      return {
        variant: 'outlined',
        color: 'inherit',
        sx: { color: 'primary.main', borderColor: 'primary.main' },
      }
  }
}

function FactoryToolbar({
  actions,
  selectedRow,
  canEdit,
  tabKey,
  onAction,
}: {
  actions: FactoryAction[]
  selectedRow: FactoryRow | null
  canEdit: boolean
  tabKey: string
  onAction: (action: FactoryAction) => void
}) {
  if (!actions.length) return null

  // PO List: row Finished (is_finished = True) khoá Edit + Delete (chỉ cho đổi
  // lại trạng thái qua nút Finished/Processing).
  const finishedLock = tabKey === 'po-list' && !!selectedRow && isRowFinished(selectedRow)

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
      {actions.map((action) => {
        // 'new' + 'mass-upload' tạo record mới → không cần selected row.
        const requiresRow = action !== 'new' && action !== 'mass-upload'
        // 'print' chỉ mở Print Preview (read-only) → account View vẫn xem được
        // chứng từ; chỉ cần selected row, không gate theo canEdit. Nút Print
        // (in thật) trong dialog preview tự ẩn khi !canEdit.
        const lockedByFinished = finishedLock && (action === 'edit' || action === 'delete')
        const disabled =
          (action !== 'print' && !canEdit) || (requiresRow && !selectedRow) || lockedByFinished
        // Nút `finished` đổi tên theo trạng thái row đang chọn (động từ hành động
        // cho rõ ý): row Finished → "Set Processing" (đổi về Processing), row
        // chưa finished → "Set Finished".
        const label =
          action === 'finished'
            ? selectedRow && isRowFinished(selectedRow)
              ? 'Set Processing'
              : 'Set Finished'
            : getActionLabel(action)
        const btn = actionButtonProps(action)
        const baseSx = {
          minWidth: 0,
          px: 0.75,
          py: 0.2,
          minHeight: 26,
          fontSize: '0.72rem',
          lineHeight: 1.1,
          textTransform: 'none' as const,
        }
        return (
          <Tooltip
            key={action}
            title={
              requiresRow && !selectedRow
                ? `${label}: select a row first`
                : lockedByFinished
                  ? `${label}: PO is finished`
                  : label
            }
          >
            <span>
              <Button
                size="small"
                variant={btn.variant}
                color={btn.color}
                disabled={disabled}
                sx={btn.sx ? { ...baseSx, ...btn.sx } : baseSx}
                onClick={() => onAction(action)}
                startIcon={getActionIcon(action)}
                title={label}
              >
                <Box component="span" sx={HIDE_ON_MOBILE_INLINE}>{label}</Box>
              </Button>
            </span>
          </Tooltip>
        )
      })}
    </Stack>
  )
}

/** Hub view của tab `data` — render selectors + các tab đã được "moved-in"
 *  thành Card buttons giống Quick Access trên Home. Hỗ trợ 2 loại card:
 *   - select: nằm trong selectors của data tab; click → set state + render
 *     workspace ngay trong /factory/<code>/data.
 *   - nav: trỏ tới 1 tab khác đã `hiddenFromTopTabs` (consumables / quality
 *     standard / deduction rate / movements); click → navigate sang URL của
 *     tab đó, workspace render với nút "Datasets" back về /factory/<code>/data. */
type DataHubMeta = { Icon: React.ComponentType<any>; color: string }
const FACTORY_DATA_HUB_META: Record<string, DataHubMeta> = {
  'item-list': { Icon: Inventory2Icon, color: '#145a59' },
  'bom': { Icon: AccountTreeIcon, color: '#4a3d8f' },
  'category-material': { Icon: CategoryIcon, color: '#a14b2a' },
  'bom-cost': { Icon: PaidIcon, color: '#5a7a4e' },
  'movement-holidays': { Icon: CalendarTodayIcon, color: '#da833c' },
  'movement-kpis': { Icon: InsightsIcon, color: '#bc2434' },
  'matching-scontract': { Icon: CompareArrowsIcon, color: '#2e6e6c' },
  'matching-pcontract': { Icon: CompareArrowsIcon, color: '#5b8f3a' },
  'consumables': { Icon: LocalShippingIcon, color: '#3d5a80' },
  'quality-standard': { Icon: RuleIcon, color: '#6b4f8f' },
  'deduction-rate': { Icon: PercentIcon, color: '#8f5a2e' },
  'internalmoving': { Icon: SwapHorizIcon, color: '#205573' },
  'report-base-goal-real': { Icon: AssessmentIcon, color: '#2e6e6c' },
  'report-activities': { Icon: BarChartIcon, color: '#a14b2a' },
  'batch-report': { Icon: BatchPredictionIcon, color: '#5a3d8f' },
  'batch-comparison': { Icon: CompareArrowsIcon, color: '#2e6e6c' },
  'batch-dashboard': { Icon: DashboardIcon, color: '#205573' },
  'batch-analysis': { Icon: AnalyticsIcon, color: '#a14b2a' },
  'batch-batch-analysis': { Icon: DonutLargeIcon, color: '#8f5a2e' },
  // 3 placeholder cards Transaction Data — render ComingSoonPage.
  'scale-data': { Icon: ScaleIcon, color: '#3d7a8f' },
  'weight-data': { Icon: MonitorWeightIcon, color: '#8f3d6e' },
}
const FACTORY_DATA_HUB_FALLBACK: DataHubMeta = { Icon: Inventory2Icon, color: '#5a5a5a' }

type DataHubCardSpec = {
  key: string
  label: string
  meta: DataHubMeta
  onClick: () => void
}

type DataHubGroupSpec = {
  key: string
  title: string
  cardRows: DataHubCardSpec[][]
}

/** Cấu trúc 4 nhóm Hub. Order bám tuần tự Access: Master Data → Movement →
 *  Matching → Consumables/Quality. Selector keys nằm trong data tab; nav
 *  keys trỏ tới các tab `hiddenFromTopTabs`. Title English-only theo yêu
 *  cầu UI 2026-05-21 — Data tab không có tiếng Việt.
 *  Mỗi phần tử `rows` là 1 dòng hiển thị trên UI. */
type DataHubItemDef = { kind: 'select'; value: string } | { kind: 'nav'; tabKey: string }
const FACTORY_DATA_HUB_LAYOUT: Array<{
  key: string
  title: string
  rows: DataHubItemDef[][]
}> = [
  {
    key: 'transaction',
    title: 'TRANSACTION DATA',
    rows: [
      // 4 card trên 1 dòng (Scale/Weight là placeholder → ComingSoonPage).
      [
        // internalmoving chuyển ra top tab (kế Lot Mgt) 2026-06-27 → bỏ khỏi hub.
        { kind: 'nav', tabKey: 'consumables' },
        { kind: 'nav', tabKey: 'scale-data' },
        { kind: 'nav', tabKey: 'weight-data' },
      ],
    ],
  },
  {
    key: 'master',
    title: 'MASTER DATA',
    // Gộp về 1 dòng (chị Đẹp). category-material / movement-holidays / movement-kpis
    // đã ẩn (selector comment ở tabs.ts) → resolveItem trả null nên tự loại khỏi dòng.
    rows: [
      [
        { kind: 'select', value: 'bom' },
        { kind: 'select', value: 'bom-cost' },
        { kind: 'nav', tabKey: 'quality-standard' },
        { kind: 'nav', tabKey: 'deduction-rate' },
      ],
    ],
  },
  {
    key: 'references',
    title: 'REFERENCES',
    rows: [
      [
        { kind: 'select', value: 'item-list' },
        { kind: 'select', value: 'matching-scontract' },
        { kind: 'select', value: 'matching-pcontract' },
      ],
    ],
  },
]

/** Cấu trúc Hub cho tab `report` — gom các report movement (trước đây nằm
 *  trong tab Movements selectors). User click 1 card → mở report tương ứng. */
const FACTORY_REPORT_HUB_LAYOUT: Array<{
  key: string
  title: string
  rows: DataHubItemDef[][]
}> = [
  {
    key: 'batch-reports',
    title: 'PRODUCTION REPORT',
    rows: [
      [
        { kind: 'select', value: 'batch-report' },
      ],
    ],
  },
  {
    key: 'movement-reports',
    title: 'MOVEMENT REPORTS',
    rows: [
      [
        { kind: 'select', value: 'report-base-goal-real' },
        { kind: 'select', value: 'report-activities' },
      ],
    ],
  },
]

function FactoryDataHubCard({ card }: { card: DataHubCardSpec }) {
  const Icon = card.meta.Icon as React.ComponentType<{ fontSize?: 'small' | 'medium' }>
  return (
    <Card
      onClick={card.onClick}
      sx={{
        height: 48,
        cursor: 'pointer',
        border: '1px solid',
        borderColor: 'divider',
        background: 'linear-gradient(180deg, rgba(255,255,255,0.98), rgba(255,249,244,0.86))',
        transition: 'transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
          borderColor: alpha(card.meta.color, 0.5),
        },
      }}
    >
      <CardContent sx={{ p: 1, '&:last-child': { pb: 1 }, height: '100%', display: 'flex', alignItems: 'center' }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ width: '100%' }}>
          <Box
            sx={{
              width: 30,
              height: 30,
              borderRadius: 1.25,
              display: 'grid',
              placeItems: 'center',
              color: card.meta.color,
              backgroundColor: alpha(card.meta.color, 0.12),
              border: `1px solid ${alpha(card.meta.color, 0.18)}`,
              flexShrink: 0,
              '& .MuiSvgIcon-root': { fontSize: '1rem' },
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Typography
            variant="body2"
            fontWeight={700}
            sx={{ lineHeight: 1.2, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis' }}
          >
            {card.label}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  )
}

function FactoryHub({
  tab,
  layout,
  onSelect,
  onNavigate,
}: {
  tab: FactoryTabConfig
  layout: Array<{ key: string; title: string; rows: DataHubItemDef[][] }>
  onSelect: (value: string) => void
  onNavigate: (tabKey: string) => void
}) {
  const selectorByValue = new Map((tab.selectors ?? []).map((s) => [s.value, s]))
  const tabByKey = new Map(FACTORY_TABS.map((t) => [t.key, t]))

  const resolveItem = (item: DataHubItemDef): DataHubCardSpec | null => {
    if (item.kind === 'select') {
      const opt = selectorByValue.get(item.value)
      if (!opt) return null
      return {
        key: `sel:${item.value}`,
        label: opt.label,
        meta: FACTORY_DATA_HUB_META[item.value] ?? FACTORY_DATA_HUB_FALLBACK,
        onClick: () => onSelect(item.value),
      }
    }
    const target = tabByKey.get(item.tabKey)
    if (!target) return null
    return {
      key: `nav:${item.tabKey}`,
      label: target.label,
      meta: FACTORY_DATA_HUB_META[item.tabKey] ?? FACTORY_DATA_HUB_FALLBACK,
      onClick: () => onNavigate(item.tabKey),
    }
  }

  const renderedGroups: DataHubGroupSpec[] = layout
    .map((group) => {
      const cardRows = group.rows
        .map((row) => row.map(resolveItem).filter((c): c is DataHubCardSpec => c !== null))
        .filter((row) => row.length > 0)
      return { key: group.key, title: group.title, cardRows }
    })
    .filter((g) => g.cardRows.length > 0)

  if (renderedGroups.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
        No items configured for this tab.
      </Typography>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 0.5 }}>
      <Stack spacing={1}>
        {renderedGroups.map((group) => (
          <Box key={group.key}>
            <Box
              sx={{
                px: 1,
                py: 0.5,
                mb: 0.5,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderLeft: 3,
                borderColor: 'primary.main',
                borderRadius: 0.5,
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={800}
                sx={{ letterSpacing: 0.04, lineHeight: 1.2 }}
              >
                {group.title}
              </Typography>
            </Box>
            <Stack spacing={0.5}>
              {group.cardRows.map((row, ri) => (
                <Box key={ri} sx={CARD_GRID_4UP}>
                  {row.map((card) => (
                    <Box key={card.key}>
                      <FactoryDataHubCard card={card} />
                    </Box>
                  ))}
                </Box>
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  )
}

function FactoryListWorkspace({
  tab,
  rows,
  loading,
  selectedRow,
  selectorValue,
  canEdit,
  crudEnabled,
  onSelectorChange,
  onSelectedRowChange,
  onAction,
  onBackToHub,
  savedColumnsByKey,
  defaultColumnsByKey,
  onColumnsPersist,
}: {
  tab: FactoryTabConfig
  rows: FactoryRow[]
  loading: boolean
  selectedRow: FactoryRow | null
  selectorValue?: string
  canEdit: boolean
  crudEnabled: boolean
  onSelectorChange: (value: string) => void
  onSelectedRowChange: (row: FactoryRow | null) => void
  onAction: (action: FactoryAction) => void
  /** Khi có → render nút "Back" thay cho dropdown selector. Dùng cho tab
   *  `data` để quay về Card hub. */
  onBackToHub?: () => void
  /** Map tabSegmentKey → visible cols, fetched từ user_preferences. */
  savedColumnsByKey?: Record<string, string[]>
  /** Map tabSegmentKey → system default cols (từ `username='default'` row).
   *  Dùng cho nút "Default columns" trong column picker. */
  defaultColumnsByKey?: Record<string, string[]>
  /** Push column visibility lên user_preferences khi user toggle/reset. */
  onColumnsPersist?: (tabSegmentKey: string, cols: string[], defaults: string[]) => void
}) {
  const selectedOption = tab.selectors?.find((option) => option.value === selectorValue)
  const baseVisible = selectedOption?.toolbarVisible === false ? [] : tab.actions
  const visibleActions =
    crudEnabled || !baseVisible.length
      ? baseVisible
      : baseVisible.filter((a) => !['new', 'edit', 'delete'].includes(a))

  /* Resolved column metadata (from tab + sampled keys + per-dataset override). */
  const colConfigs = useMemo(
    () => resolveListColumnConfigs(tab, rows, selectorValue),
    [tab, rows, selectorValue],
  )
  const colsByField = useMemo(() => {
    const m = new Map<string, FactoryColumnConfig>()
    for (const c of colConfigs) m.set(c.field, c)
    return m
  }, [colConfigs])

  const widthStorageKey = useMemo(
    () => `factory.${tab.key}${selectorValue ? `.${selectorValue}` : ''}`,
    [tab.key, selectorValue],
  )
  const { getWidth, startResize } = useColumnWidths(widthStorageKey)

  /* Freeze (sticky-left) cột định danh theo tab. Skill `/table-style` §7.6.
   *  `frozenOrder` + `firstFrozenWidth` được compute SAU `visibleColumns`
   *  (xem block phía dưới) — chỉ khai báo keys + fallback widths ở đây. */
  const isMobile = useIsMobile()
  const frozenKeys = FROZEN_KEYS_BY_TAB[tab.key] ?? (new Set<string>() as ReadonlySet<string>)
  const frozenFallbackWidths: Record<string, number> =
    (isMobile ? FROZEN_FALLBACK_WIDTH_MOBILE_BY_TAB : FROZEN_FALLBACK_WIDTH_DESKTOP_BY_TAB)[tab.key] ?? {}

  /** Menu columns ({key, label}) cho `FuturesColumnsMenu` — render từ colConfigs. */
  const menuColumns = useMemo(
    () => colConfigs.map((c) => ({ key: c.field, label: c.headerName })),
    [colConfigs],
  )

  // Skill /user-logaction (2026-05-26): generic registry replaces
  // useFactoryDatasheetColumns + onColumnsPersist auto-seed. Sub-key = tab.key
  // (cross-factory share — chốt với chị). Selector value (vd 'bom-cost')
  // không vào subKey vì hub tabs ('data'/'report') không có columns.
  // Baseline `(factory, columns_v2)` đã seed 10 mode qua migration 0017.
  const columnsCtrl = usePreferenceColumns({
    scope: 'factory',
    prefKey: 'columns',
    subKey: tab.key,
    allColumns: menuColumns,
    widthsTableKey: widthStorageKey,
  })
  const { visibleColumns: visibleMenuColumns, openMenu: openColMenu } = columnsCtrl

  // factoryColumnStorageKey + savedColumnsByKey + onColumnsPersist (legacy
  // factory navigation column pref API) đã được generic registry thay thế.
  // Em giữ unused-suppress để không xoá nhầm props chưa cleanup từ parent.
  void factoryColumnStorageKey
  void savedColumnsByKey
  void defaultColumnsByKey
  void onColumnsPersist

  /* Filter + sort + paginate (matches SaleContract pattern). */
  const [searchTerm, setSearchTerm] = useState('')
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)
  const [sortState, setSortState] = useState<TableSortState<string> | null>(null)

  const filterPopover = useFilterPopover<'search'>()
  const columnFilters = useColumnFilters()
  const [colPopover, setColPopover] = useState<{
    key: string
    position: ColumnFilterAnchorPosition
  } | null>(null)

  // Reset filters when tab/dataset changes.
  useEffect(() => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    setSortState(null)
  }, [tab.key, selectorValue])  // eslint-disable-line react-hooks/exhaustive-deps

  const visibleColumns = useMemo(
    () =>
      visibleMenuColumns
        .map((mc) => colsByField.get(mc.key))
        .filter((c): c is FactoryColumnConfig => c != null),
    [visibleMenuColumns, colsByField],
  )

  /** Thứ tự cột freeze theo `visibleColumns` — dùng để compute offset `left`
   *  của cột thứ 2 (Issue tab: stack_id rồi po_no). KHÔNG hardcode thứ tự —
   *  đi theo visibleColumns sau khi user reorder. */
  const frozenOrder: string[] = useMemo(
    () => visibleColumns.map((c) => c.field).filter((k) => frozenKeys.has(k)),
    [visibleColumns, frozenKeys],
  )
  /** Width của cột freeze đầu — fallback nếu user chưa drag-resize. Dùng cho
   *  inline `style.left` của cột freeze thứ 2 (Issue tab). */
  const firstFrozenWidth: number =
    frozenOrder.length === 0
      ? 0
      : getWidth(frozenOrder[0]) ?? frozenFallbackWidths[frozenOrder[0]] ?? 0

  // Search columns shape khớp `rowMatchesSearch` template signature.
  const searchColumns = useMemo(
    () => colConfigs.map((c) => ({ key: c.field, kind: c.type })),
    [colConfigs],
  )

  const filteredRows = useMemo(
    () =>
      rows.filter((r) => {
        if (!rowMatchesSearch(r, searchTerm, searchColumns as never)) return false
        if (!rowMatchesAllColumnFilters(r, columnFilters.filters)) return false
        return true
      }),
    [rows, searchTerm, searchColumns, columnFilters.filters],
  )

  const popoverColumn = useMemo(
    () => (colPopover ? colsByField.get(colPopover.key) ?? null : null),
    [colPopover, colsByField],
  )
  const popoverKind = useMemo<ColumnFilterKind>(
    () =>
      popoverColumn
        ? inferColumnFilterKind(
            { key: popoverColumn.field, kind: popoverColumn.type } as never,
            rows,
          )
        : 'text',
    [popoverColumn, rows],
  )
  const popoverOptions = useMemo(
    () =>
      popoverKind === 'checkboxes' && colPopover
        ? distinctColumnValues(rows, colPopover.key)
        : undefined,
    [popoverKind, colPopover, rows],
  )

  /* Sort columns by their declared type (date/number/string). */
  const sortableColumns = useMemo(
    () =>
      visibleColumns.map((c) => ({
        key: c.field,
        kind:
          c.type === 'date'
            ? ('date' as const)
            : c.type === 'number'
              ? ('number' as const)
              : ('text' as const),
      })),
    [visibleColumns],
  )
  const sortedRows = useMemo(
    () => sortTableRows(filteredRows, sortState, sortableColumns),
    [filteredRows, sortState, sortableColumns],
  )
  const pageRows = useMemo(
    () =>
      rowsPerPage === -1
        ? sortedRows
        : sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [page, rowsPerPage, sortedRows],
  )
  const toggleSort = (key: string) => {
    setSortState((cur) => toggleTableSort(cur, key))
    setPage(0)
  }

  /* Footer totals — map Access `AggregateType`:
   *   - `aggregate: 'sum'`  → SUM (Access AggregateType=0)
   *   - `aggregate: 'none'` → bỏ qua (cell trống)
   *   - unset & type='number' → fallback SUM (giữ hành vi tab chưa migrate). */
  const totalRowValues = useMemo(() => {
    const m = new Map<string, number | null>()
    for (const c of visibleColumns) {
      if (c.aggregate === 'none') continue
      if (c.aggregate !== 'sum' && c.type !== 'number') continue
      m.set(c.field, sumNumericColumn(filteredRows, c.field))
    }
    return m
  }, [filteredRows, visibleColumns])

  // Default width mỗi cột THƯỜNG = đo theo độ dài text thật (header + sample
  // data) → cột text ngắn hiển thị gọn 1 dòng, cột dài bị giới hạn MAX rồi wrap.
  // Cột freeze dùng frozenFallbackWidths riêng; cột user đã resize → getWidth.
  const autoColWidths = useMemo<Record<string, number>>(() => {
    const CHAR_PX = 7.7 // bề rộng ~1 ký tự ở fontSize 0.8125rem (rộng rãi hơn)
    const HEADER_PAD = 52 // padding + sort suffix + filter icon + resize handle
    const CELL_PAD = 28 // padding 2 bên cell body
    const MIN = 100 // sàn tối thiểu — tránh cột quá hẹp
    const MAX = 260
    const sample = filteredRows.slice(0, 400)
    const out: Record<string, number> = {}
    for (const col of visibleColumns) {
      const meta = colsByField.get(col.field)
      // Cột khai báo fixedWidth → ghi đè auto-fit (vẫn thua user resize tay).
      if (meta?.fixedWidth != null) {
        out[col.field] = meta.fixedWidth
        continue
      }
      const header = col.headerName ?? col.field
      let bodyMax = 0
      for (const r of sample) {
        const len = formatCell(r[col.field], meta?.type).length
        if (len > bodyMax) bodyMax = len
      }
      const headerPx = header.length * CHAR_PX + HEADER_PAD
      const bodyPx = bodyMax * CHAR_PX + CELL_PAD
      // Cột ngày (DD-MMM-YYYY) cần rộng hơn 1 xíu cho thoáng, không sát chữ.
      const minForType = meta?.type === 'date' || meta?.type === 'datetime' ? 124 : MIN
      out[col.field] = Math.round(Math.min(MAX, Math.max(minForType, headerPx, bodyPx)))
    }
    return out
  }, [visibleColumns, colsByField, filteredRows])

  // minTableWidth = tổng width hiệu lực (user-resize → freeze fallback → auto).
  const minTableWidth = useMemo(() => {
    let sum = 0
    for (const col of visibleColumns) {
      const k = col.field
      const eff =
        getWidth(k) ??
        (frozenKeys.has(k) ? frozenFallbackWidths[k] : autoColWidths[k]) ??
        140
      sum += eff
    }
    return Math.max(720, sum)
  }, [visibleColumns, getWidth, frozenKeys, frozenFallbackWidths, autoColWidths])

  const hasActiveFilters = !!searchTerm.trim() || columnFilters.hasAny
  const activeFilterCount = (searchTerm.trim() ? 1 : 0) + columnFilters.activeKeys.length
  const clearAllFilters = () => {
    setSearchTerm('')
    columnFilters.clearAll()
    setPage(0)
    filterPopover.close()
  }
  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}:${visibleColumns.length}`
  const selectedId = selectedRow?.id ?? null

  /** ID range text (match SaleContract ContractListView idRangeText). Hiển thị "ID: min–max"
   *  của trang hiện tại. Chỉ áp dụng khi ID numeric. */
  const idRangeText = useMemo(() => {
    if (pageRows.length === 0) return 'ID: —'
    let min = Number.POSITIVE_INFINITY
    let max = Number.NEGATIVE_INFINITY
    for (const r of pageRows) {
      const id = Number(r.id)
      if (!Number.isFinite(id)) continue
      if (id < min) min = id
      if (id > max) max = id
    }
    if (!Number.isFinite(min) || !Number.isFinite(max)) return 'ID: —'
    return `ID: ${min}–${max}`
  }, [pageRows])

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        // Bảng FILL hết không gian flex còn lại (chuỗi Card→CardContent đã bounded)
        // thay vì clamp height theo viewport → bỏ khoảng trống dưới bảng. Override
        // clamp của `.app-dataframe` (chỉ workspace này).
        '& .app-dataframe': { flex: 1, minHeight: 0, height: 'auto' },
      }}
    >
      <FilterBar
        hasActiveFilters={hasActiveFilters}
        onClearAll={clearAllFilters}
        activeFilterCount={activeFilterCount}
        rightSlot={
          <FactoryToolbar
            actions={visibleActions}
            selectedRow={selectedRow}
            canEdit={canEdit}
            tabKey={tab.key}
            onAction={onAction}
          />
        }
      >
        {/* Thống nhất Data hub: Back "Datasets" + pill ĐẦU TIÊN, rồi Search
            (Datasets-first) — Search render sau block onBackToHub bên dưới. */}
        {onBackToHub ? (
          <>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              startIcon={<ArrowBackIcon fontSize="small" />}
              onClick={onBackToHub}
              sx={filterBarBtnSxTemplate}
            >
              Datasets
            </Button>
            {/* Accent pill tên card — phân biệt page khi mở từ Data hub (đồng
                bộ FactoryBomPage / DeductionRate / SContractTab). */}
            {(() => {
              const meta = FACTORY_DATA_HUB_META[tab.key] ?? FACTORY_DATA_HUB_FALLBACK
              const AccentIcon = meta.Icon as React.ComponentType<{ sx?: SxProps<Theme> }>
              return (
                <Stack
                  direction="row"
                  spacing={0.625}
                  alignItems="center"
                  sx={{
                    mx: 0.5,
                    px: 1,
                    py: 0.5,
                    borderRadius: 1,
                    backgroundColor: meta.color,
                    color: '#fff',
                    boxShadow: `0 1px 3px ${alpha(meta.color, 0.45)}`,
                  }}
                >
                  <AccentIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
                  <Typography
                    variant="subtitle2"
                    sx={{
                      fontWeight: 800,
                      fontSize: '0.85rem',
                      color: '#fff',
                      textTransform: 'uppercase',
                      letterSpacing: 0.6,
                      whiteSpace: 'nowrap',
                      lineHeight: 1.1,
                    }}
                  >
                    {tab.label}
                  </Typography>
                </Stack>
              )
            })()}
          </>
        ) : null}
        <FilterButton
          label="Search"
          active={!!searchTerm.trim()}
          onClick={(e) => filterPopover.open('search', e)}
        />
        {tab.selectors && tab.selectors.length > 1 && tab.key !== 'data' ? (
          <Autocomplete
            size="small"
            options={tab.selectors}
            value={tab.selectors.find((o) => o.value === selectorValue) ?? tab.selectors[0] ?? null}
            onChange={(_e, v) => { if (v) onSelectorChange(v.value) }}
            getOptionLabel={(o) => o ? `${o.value} — ${o.label}` : ''}
            isOptionEqualToValue={(a, b) => a.value === b.value}
            disableClearable
            sx={{
              minWidth: 200,
              '& .MuiInputBase-root': { minHeight: 26, fontSize: '0.7rem' },
              '& .MuiInputLabel-root': { fontSize: '0.7rem' },
            }}
            renderInput={(params) => <TextField {...params} label="Dataset" />}
          />
        ) : null}
        {menuColumns.length ? (
          <Button
            size="small"
            variant="outlined"
            endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />}
            onClick={(e) => openColMenu(e.currentTarget)}
            sx={filterBarBtnSxTemplate}
            title="Columns"
          >
            <Box component="span" sx={HIDE_ON_TABLET_INLINE}>Columns</Box>
            <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>Cols</Box>
          </Button>
        ) : null}
      </FilterBar>

      <Divider sx={{ mb: 0.5 }} />

      <Box className="app-dataframe">
        {/* Status counters + pagination */}
        <Box
          className="app-dataframe-toolbar"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 0.5,
            flexWrap: 'wrap',
            minHeight: 24,
            py: 0,
          }}
        >
          <Stack direction="row" alignItems="center" spacing={0.4} sx={{ flexShrink: 0 }}>
            {loading && <CircularProgress size={15} />}
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Total: {filteredRows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              Loaded: {rows.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontWeight: 400, whiteSpace: 'nowrap', mr: 0.5, fontSize: '0.68rem', ...HIDE_ON_MOBILE_INLINE }}
            >
              {idRangeText}
            </Typography>
            <TablePagination
              component="div"
              count={filteredRows.length}
              page={rowsPerPage === -1 ? 0 : page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(Number(e.target.value))
                setPage(0)
              }}
              rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]}
              labelRowsPerPage="Rows"
              sx={TABLE_PAGINATION_COMPACT}
            />
          </Stack>
        </Box>

        <FilterPopover
          open={filterPopover.state !== null}
          anchorEl={filterPopover.state?.anchor ?? null}
          onClose={filterPopover.close}
          maxWidth={320}
        >
          {filterPopover.isOpen('search') && (
            <SearchFilterContent
              value={searchTerm}
              onChange={(v) => { setSearchTerm(v); setPage(0) }}
              placeholder="Search across all columns..."
              label="Search"
              onClear={filterPopover.close}
            />
          )}
        </FilterPopover>

        <ColumnFilterPopover
          open={colPopover !== null}
          anchorPosition={colPopover?.position ?? null}
          onClose={() => setColPopover(null)}
          label={popoverColumn?.headerName ?? ''}
          kind={popoverKind === 'none' ? 'text' : popoverKind}
          value={colPopover ? columnFilters.filters[colPopover.key] : undefined}
          onChange={(v) => {
            if (!colPopover) return
            columnFilters.setFilter(colPopover.key, v)
            setPage(0)
          }}
          options={popoverOptions}
        />

        {/* Columns menu — drag/drop reorder + toggle visibility (parity SaleContract). */}
        <FuturesColumnsMenu controller={columnsCtrl} />

        {/* Virtualized table — SaleContract layout */}
        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          {loading ? (
            <BrandState tone="loading" title="Loading…" description="Please wait." />
          ) : pageRows.length === 0 ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 0.5,
                height: '100%',
                minHeight: 200,
                color: 'text.secondary',
                fontSize: '0.85rem',
                textAlign: 'center',
                px: 2,
              }}
            >
              {/* Phân biệt: data rỗng (date range) vs bị filter/search ẩn hết.
                  `rows` = raw từ API (sau date filter); pageRows = sau search/col. */}
              {rows.length === 0 ? (
                <>
                  <Box component="span" sx={{ fontWeight: 600 }}>No records found</Box>
                  <Box component="span">
                    No data in the selected date range. Adjust the date range above to see more.
                  </Box>
                </>
              ) : (
                <Box component="span">No rows match the current search or filters.</Box>
              )}
            </Box>
          ) : (
            <TableVirtuoso
              key={gridKey}
              style={{ height: '100%' }}
              data={pageRows}
              fixedHeaderContent={() => (
                <TableRow>
                  {visibleColumns.map((col) => {
                    const key = col.field
                    const isFrozen = frozenKeys.has(key)
                    const w = getWidth(key) ?? (isFrozen ? frozenFallbackWidths[key] : autoColWidths[key])
                    // Freeze header — inline style (specificity 1,0,0,0 luôn thắng
                    // MUI `.MuiTableCell-stickyHeader` z-index 2 + emotion class).
                    // Skill `/table-style` §7.6.
                    const headerStyle: CSSProperties = {}
                    if (w) {
                      headerStyle.width = w
                      headerStyle.minWidth = w
                      headerStyle.maxWidth = w
                    }
                    if (isFrozen) {
                      headerStyle.position = 'sticky'
                      headerStyle.top = 0
                      // Cột freeze đầu → left: 0. Cột freeze thứ 2 (Issue: po_no
                      // sau stack_id) → left = width của cột đầu.
                      headerStyle.left = frozenOrder.indexOf(key) === 0 ? 0 : firstFrozenWidth
                      headerStyle.zIndex = 100
                    }
                    return (
                      <TableCell
                        key={col.field}
                        align={col.align}
                        className="app-table-header-cell"
                        onClick={() => toggleSort(col.field)}
                        style={Object.keys(headerStyle).length ? headerStyle : undefined}
                        sx={{
                          whiteSpace: 'nowrap',
                          cursor: 'pointer',
                          // Cho phép bôi đen + copy TÊN CỘT (click-sort vẫn chạy).
                          userSelect: 'text',
                          ...(isFrozen ? {} : { position: 'relative' }),
                        }}
                      >
                        {col.headerName}
                        {getTableSortSuffix(sortState, col.field)}
                        <ColumnFilterIcon
                          active={columnFilters.isActive(col.field)}
                          onClick={(position) => setColPopover({ key: col.field, position })}
                          ariaLabel={`Filter ${col.headerName}`}
                        />
                        <ResizeHandle onMouseDown={(e) => startResize(col.field, e)} />
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              fixedFooterContent={() => (
                <TableRow>
                  {visibleColumns.map((col, index) => {
                    const key = col.field
                    const isFrozen = frozenKeys.has(key)
                    const total = totalRowValues.get(col.field)
                    const val = total == null ? '' : formatAccessNumber(total)
                    const w = getWidth(key) ?? (isFrozen ? frozenFallbackWidths[key] : autoColWidths[key])
                    // Freeze footer — className handle sticky/bg/z-index qua
                    // `.app-dataframe tfoot td.app-sticky-col[-2]` (CSS shared).
                    // Cột freeze thứ 2: cần inline `style.left` = width cột 1.
                    const frozenIdx = isFrozen ? frozenOrder.indexOf(key) : -1
                    const stickyClass =
                      frozenIdx === 0 ? 'app-sticky-col' : frozenIdx === 1 ? 'app-sticky-col-2' : undefined
                    const cellStyle: CSSProperties = {}
                    if (w) {
                      cellStyle.width = w
                      cellStyle.minWidth = w
                      cellStyle.maxWidth = w
                    }
                    if (frozenIdx === 1) cellStyle.left = firstFrozenWidth
                    return (
                      <TableCell
                        key={col.field}
                        align={col.align}
                        className={stickyClass}
                        style={Object.keys(cellStyle).length ? cellStyle : undefined}
                        sx={{
                          fontWeight: 700,
                          whiteSpace: 'nowrap',
                          py: 0.5,
                          bgcolor: 'background.paper',
                          borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {index === 0 ? 'Total' : val}
                      </TableCell>
                    )
                  })}
                </TableRow>
              )}
              itemContent={(_: number, row: FactoryRow) =>
                visibleColumns.map((col) => {
                  const key = col.field
                  const isFrozen = frozenKeys.has(key)
                  const meta = colsByField.get(col.field)
                  const val = formatCell(row[col.field], meta?.type)
                  const w = getWidth(key) ?? (isFrozen ? frozenFallbackWidths[key] : autoColWidths[key])
                  // Freeze body — className handle sticky/bg/hover/selected qua
                  // `.app-dataframe tbody td.app-sticky-col[-2]` (CSS shared).
                  // Cột freeze thứ 2: cần inline `style.left` = width cột 1.
                  const frozenIdx = isFrozen ? frozenOrder.indexOf(key) : -1
                  const stickyClass =
                    frozenIdx === 0 ? 'app-sticky-col' : frozenIdx === 1 ? 'app-sticky-col-2' : undefined
                  const cellStyle: CSSProperties = {}
                  if (w) {
                    cellStyle.width = w
                    cellStyle.minWidth = w
                    cellStyle.maxWidth = w
                  }
                  if (frozenIdx === 1) cellStyle.left = firstFrozenWidth
                  // Wrap MỌI cột: text dài xuống dòng trong width cột thay vì cắt
                  // "…"; TableVirtuoso đo chiều cao động → row tự cao (autoheight).
                  return (
                    <TableCell
                      key={col.field}
                      align={col.align}
                      className={stickyClass}
                      style={Object.keys(cellStyle).length ? cellStyle : undefined}
                      sx={{ whiteSpace: 'normal', overflowWrap: 'anywhere', py: 0.5, verticalAlign: 'top' }}
                    >
                      <Box component="span" className="app-table-cell-wrap">
                        {val || '-'}
                      </Box>
                    </TableCell>
                  )
                })
              }
              components={{
                Table: (props: any) => (
                  <Table {...props} size="small" stickyHeader sx={{ minWidth: minTableWidth, width: '100%' }} />
                ),
                TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
                TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
                TableRow: (props: any) => {
                  const idx = Number((props as any)['data-item-index'])
                  const rowId = Number.isFinite(idx) ? pageRows[idx]?.id : null
                  return (
                    <TableRow
                      {...props}
                      hover
                      selected={rowId != null && rowId === selectedId}
                      sx={{ cursor: 'pointer' }}
                      onClick={() => {
                        if (rowId == null) return
                        const next = pageRows.find((r) => r.id === rowId) ?? null
                        onSelectedRowChange(next)
                      }}
                    />
                  )
                },
              }}
            />
          )}
        </Box>
      </Box>
    </Box>
  )
}

/* ------------------------------------------------------------------ */
/*  Style helpers — copy nguyên từ SaleContract ContractFormDialog     */
/* ------------------------------------------------------------------ */

const fmtNumStr = (s: string): string => {
  if (!s) return ''
  const neg = s.startsWith('-')
  const abs = neg ? s.slice(1) : s
  const [intPart, ...rest] = abs.split('.')
  const intFormatted = intPart ? Number(intPart).toLocaleString('en-US') : '0'
  const result = rest.length > 0 ? `${intFormatted}.${rest.join('.')}` : intFormatted
  return neg ? `-${result}` : result
}

const numSx = { '& input': { textAlign: 'right' as const } } as const

/** Compact field sx áp dụng vào DialogContent — y hệt SaleContract. */
const compactContentSx = {
  pt: 0.75,
  '& .MuiInputBase-root': { minHeight: 32, fontSize: '0.8125rem' },
  '& .MuiInputBase-input': { py: 0.5, fontSize: '0.8125rem' },
  '& .MuiInputLabel-root': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiInputLabel-shrink': { fontWeight: 600 },
  '& .MuiFormControlLabel-label': { fontSize: '0.8125rem', fontWeight: 500 },
  '& .MuiCheckbox-root': { p: 0.5 },
  '& .MuiSvgIcon-root': { fontSize: '1rem' },
  '& .MuiTypography-caption': {
    fontSize: '0.6875rem',
    fontWeight: 600,
    letterSpacing: '0.03em',
    textTransform: 'uppercase',
  },
  '& .MuiTypography-body2': { fontSize: '0.8125rem' },
  '& .MuiSelect-select': { fontSize: '0.8125rem' },
} as const

const titleBtnSx = {
  py: 0.25,
  px: 1,
  minHeight: 28,
  fontSize: '0.75rem',
  lineHeight: 1.2,
  textTransform: 'none' as const,
}

function ActionDialog({
  pendingAction,
  saving,
  onClose,
  onSaveNew,
  onSaveEdit,
}: {
  pendingAction: PendingAction | null
  saving: boolean
  onClose: () => void
  onSaveNew: (payload: FactoryBRowPayload) => void
  onSaveEdit: (payload: FactoryBRowPayload) => void
}) {
  const [draft, setDraft] = useState<Record<string, string>>({})

  useEffect(() => {
    if (!pendingAction || pendingAction.action === 'delete') return
    const { action, row, tab } = pendingAction
    if (action === 'new') {
      const next: Record<string, string> = {}
      for (const col of tab.columns) {
        next[col.field] = ''
      }
      setDraft(next)
      return
    }
    if ((action === 'edit' || action === 'quality') && row) {
      const next: Record<string, string> = {}
      for (const [field, value] of Object.entries(row)) {
        next[field] = stringifyCell(value as FactoryCell)
      }
      setDraft(next)
    }
  }, [pendingAction])

  if (!pendingAction || pendingAction.action === 'delete') return null

  const { action, tab } = pendingAction
  const isWrite = action === 'new' || action === 'edit' || action === 'quality'

  const handleSave = () => {
    const payload = draftToPayload(draft)
    if (action === 'new') onSaveNew(payload)
    if (action === 'edit' || action === 'quality') onSaveEdit(payload)
  }

  /** Build danh sách field theo thứ tự cột Access trước, sau đó các field row dư.
   *  Mỗi field tham chiếu lại tab.columns để biết label + type (date/number). */
  const colByField = new Map<string, FactoryColumnConfig>()
  for (const c of tab.columns) colByField.set(c.field, c)
  const draftKeys = Object.keys(draft)
  const orderedFields = [
    ...tab.columns.filter((c) => draftKeys.includes(c.field)).map((c) => c.field),
    ...draftKeys.filter((k) => !colByField.has(k)),
  ]
  const renderFields = orderedFields.length
    ? orderedFields
    : tab.columns.slice(0, 8).map((c) => c.field)

  const dialogTitle = `${getActionLabel(action)} — ${tab.label}`

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          maxHeight: 'min(900px, 92vh)',
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {dialogTitle}
        </Typography>
        <Chip
          size="small"
          label={tab.accessTarget}
          sx={{ fontSize: '0.7rem', height: 22, mr: 0.5 }}
        />
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          {isWrite && (
            <Button
              variant="contained"
              size="small"
              color="success"
              onClick={handleSave}
              disabled={saving}
              sx={titleBtnSx}
            >
              {saving ? <CircularProgress size={14} color="inherit" /> : 'Save'}
            </Button>
          )}
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={saving}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent
        dividers
        sx={{ ...compactContentSx, overflowY: 'auto', flex: '1 1 auto', minHeight: 0 }}
      >
        <Box sx={{ maxWidth: 980, mx: 'auto', pb: 1.5 }}>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))' },
              gap: 0.75,
            }}
          >
            {renderFields.map((field) => {
              const meta = colByField.get(field)
              const label = meta?.headerName ?? humanizeFieldKey(field)
              const raw = draft[field] ?? ''
              const disabled = !isWrite
              const handleChange = (val: string) =>
                setDraft((prev) => ({ ...prev, [field]: val }))

              if (meta?.type === 'date') {
                return (
                  <DateTextField
                    key={field}
                    label={label}
                    size="small"
                    fullWidth
                    value={raw}
                    onChange={(v) => handleChange(v)}
                    disabled={disabled}
                  />
                )
              }
              if (meta?.type === 'number') {
                return (
                  <TextField
                    key={field}
                    label={label}
                    size="small"
                    fullWidth
                    value={fmtNumStr(raw)}
                    onChange={(e) => handleChange(e.target.value.replace(/,/g, ''))}
                    disabled={disabled}
                    inputProps={{ inputMode: 'decimal' }}
                    sx={numSx}
                  />
                )
              }
              return (
                <TextField
                  key={field}
                  label={label}
                  size="small"
                  fullWidth
                  value={raw}
                  onChange={(e) => handleChange(e.target.value)}
                  disabled={disabled}
                />
              )
            })}
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  )
}

function validateCopyId(value: string, fromLabel: string): string | null {
  const trimmed = value.trim()
  if (!trimmed) return 'New code is required'
  if (/\s/.test(trimmed)) return 'Code must not contain whitespace'
  if (trimmed === fromLabel.trim()) return 'New code must differ from the source code'
  if (trimmed.length > 64) return 'Code must not exceed 64 characters'
  return null
}

function CopyIdDialog({
  open,
  kind,
  busy,
  fromLabel,
  onClose,
  onConfirm,
}: {
  open: boolean
  kind: 'receive' | 'delivery' | 'consumables' | null
  busy: boolean
  fromLabel: string
  onClose: () => void
  onConfirm: (newId: string) => void
}) {
  const [value, setValue] = useState('')
  const [touched, setTouched] = useState(false)

  useEffect(() => {
    if (open) {
      setValue('')
      setTouched(false)
    }
  }, [open])

  if (!open || !kind) return null

  const title =
    kind === 'receive' ? 'Copy GRN' : kind === 'delivery' ? 'Copy GDN' : 'Copy MRN/MDN'

  const label =
    kind === 'receive'
      ? 'New GRN code'
      : kind === 'delivery'
        ? 'New GDN code'
        : 'New MRN/MDN number'

  const validationError = validateCopyId(value, fromLabel)
  const showError = touched && validationError !== null

  const handleConfirm = () => {
    setTouched(true)
    if (!validationError) onConfirm(value.trim())
  }

  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth scroll="paper">
      <DialogTitle
        sx={{
          pb: 1,
          pt: 1.25,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          flexShrink: 0,
          flexWrap: 'nowrap',
        }}
      >
        <Typography
          component="span"
          sx={{ flex: '1 1 auto', fontWeight: 700, fontSize: '1rem', minWidth: 0 }}
        >
          {title}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', ml: 'auto', flexShrink: 0 }}>
          <Button
            variant="contained"
            size="small"
            color="success"
            disabled={busy || validationError !== null}
            onClick={handleConfirm}
            sx={titleBtnSx}
          >
            {busy ? <CircularProgress size={14} color="inherit" /> : 'Copy'}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={onClose}
            disabled={busy}
            sx={titleBtnSx}
          >
            Cancel
          </Button>
        </Box>
      </DialogTitle>
      <Divider sx={{ flexShrink: 0 }} />
      <DialogContent dividers sx={compactContentSx}>
        <Box sx={{ maxWidth: 420, mx: 'auto', pb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.75 }}>
            Nguồn: {fromLabel}
          </Typography>
          <TextField
            label={label}
            value={value}
            onChange={(e) => {
              setValue(e.target.value)
              if (!touched) setTouched(true)
            }}
            onBlur={() => setTouched(true)}
            size="small"
            fullWidth
            autoFocus
            error={showError}
            helperText={showError ? validationError : ' '}
          />
        </Box>
      </DialogContent>
    </Dialog>
  )
}

export default function FactoryPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const snack = useSnackbar()
  const dispatch = useAppDispatch()
  const permission = usePermission('factoryBl')
  // factoryCode = segment[0] của URL (`/baoloc/...`, `/nhontrach/...`). App.tsx
  // dùng route LITERAL per factory (không còn param `:factoryCode`) nên useParams
  // rỗng → phải parse pathname. Whitelist FACTORY_CODES + fallback 'baoloc' cho
  // path lạ. (Bug cũ: đọc useParams → mọi factory đều ra data Bảo Lộc.)
  const factoryCode = useMemo(() => {
    const seg = location.pathname.split('/').filter(Boolean)[0]
    return seg && FACTORY_CODES.has(seg) ? seg : 'baoloc'
  }, [location.pathname])

  const [filters, setFilters] = useState<FactoryDateRange>(getDefaultDateRange)
  const [selectorValues, setSelectorValues] = useState<Record<string, string>>({})
  const [selectedRow, setSelectedRow] = useState<FactoryRow | null>(null)
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)
  // PO List: confirm đổi Processing↔Finished (nút riêng, popup như Batch Report).
  const [finishedConfirm, setFinishedConfirm] = useState<{ row: FactoryRow; next: boolean } | null>(null)
  const [finishedSaving, setFinishedSaving] = useState(false)
  const [massUploadOpen, setMassUploadOpen] = useState(false)
  const [copyTarget, setCopyTarget] = useState<{
    kind: 'receive' | 'delivery' | 'consumables'
    row: FactoryRow
  } | null>(null)
  const [exporting, setExporting] = useState<'all' | 'filter' | null>(null)
  const [serverDatesHydrated, setServerDatesHydrated] = useState(false)
  // Receive Quality dialog state — bám flow Access btnQC_Click:
  //   `qualityFetching` = đang gọi GET receive-quality.
  //   `qualityConfirm` = đã có QC, đợi user chọn Yes (mở edit) / No (mở view).
  //   `qualityDialog` = state mở dialog FactoryReceiveQualityDialog.
  const [qualityFetching, setQualityFetching] = useState(false)
  const [qualityConfirm, setQualityConfirm] = useState<
    { grnId: string; row: Record<string, FactoryCell> } | null
  >(null)
  const [qualityDialog, setQualityDialog] = useState<
    | {
        mode: QualityDialogMode
        grnId: string
        row: Record<string, FactoryCell>
      }
    | null
  >(null)
  // Receive Print dialog state — Access btnPrintRp_Click → mở preview dialog
  // (same pattern as Sales Contract Print-Off/Print-On).
  const [printReceiveGrnId, setPrintReceiveGrnId] = useState<string | null>(null)
  // Delivery (GDN) Quality + Print dialog state — mirror Receive flow, keyed
  // bằng gdn_id. Access `frmFactoryDeliverySubForm` btnQuality / btnPrintRp.
  const [deliveryQualityFetching, setDeliveryQualityFetching] = useState(false)
  const [deliveryQualityDialog, setDeliveryQualityDialog] = useState<
    | {
        mode: DeliveryQualityDialogMode
        gdnId: string
        row: Record<string, FactoryCell>
      }
    | null
  >(null)
  const [printDeliveryId, setPrintDeliveryId] = useState<number | null>(null)
  const [printDeliveryGdn, setPrintDeliveryGdn] = useState<string | null>(null)
  const email = useAppSelector((s) => s.auth.user?.email ?? '')

  const { activeKey, urlSelectorSegment } = useMemo(() => {
    // URL pattern (2026-05-28): `/<factoryCode>/<tabKey>/<selector>/<inner...>`.
    // segments[0]=factoryCode, [1]=tabKey, [2]=selector, [3+]=inner.
    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    const tabKey = isFactoryTabKey(candidate) ? candidate : DEFAULT_FACTORY_TAB
    return { activeKey: tabKey, urlSelectorSegment: segments[2] }
  }, [location.pathname])

  const activeTab = FACTORY_TABS.find((tab) => tab.key === activeKey) ?? FACTORY_TABS[0]
  // Zoom report Summary — control nằm trên block Tabs chung (góc phải), cùng
  // hook + style với dashboard PnL. State session, chỉ áp cho tab summary.
  const { zoom: summaryZoom, control: summaryZoomControl } = useReportZoom()
  // Hub-style tabs (`data`, `report`) drive dataset từ URL segment thứ 3 —
  // `/factory/<code>/data/<dataset>`. Không có segment → render hub (Card
  // grid). Selector tab khác (vd `movements`) giữ state-driven: auto-fallback
  // option đầu khi chưa chọn.
  const isHubTab = activeTab.key === 'data' || activeTab.key === 'report'
  const urlSelectorValue = useMemo(() => {
    if (!isHubTab || !urlSelectorSegment) return undefined
    return activeTab.selectors?.some((s) => s.value === urlSelectorSegment)
      ? urlSelectorSegment
      : undefined
  }, [isHubTab, urlSelectorSegment, activeTab])
  const selectorValue = activeTab.selectors
    ? isHubTab
      ? urlSelectorValue
      : selectorValues[activeTab.key] ?? activeTab.selectors[0]?.value
    : undefined
  // Merge URL-driven hub selector vào map state để CRUD handlers
  // (`resolveDatasetForTab`) đọc thống nhất qua `selectorState[tab.key]`.
  const selectorValuesEffective = useMemo(() => {
    if (!isHubTab) return selectorValues
    return { ...selectorValues, [activeTab.key]: selectorValue ?? '' }
  }, [selectorValues, isHubTab, activeTab.key, selectorValue])
  const inDataHub = activeTab.key === 'data' && !selectorValue
  const inReportHub = activeTab.key === 'report' && !selectorValue
  const inHub = inDataHub || inReportHub
  const invalidRange = filters.startDate > filters.endDate

  const { data: contextData } = useGetFactoryContextQuery(factoryCode)
  // True khi chưa có context CỦA factory hiện tại — initial load hoặc vừa
  // chuyển factory. Khi đã có sẽ ẩn loader luôn (refetch nền không blank
  // page). So sánh factoryCode trong response với param URL.
  const switchingFactory = !contextData || contextData.factoryCode !== factoryCode
  const [saveDateRange] = useSaveFactoryDateRangeMutation()
  const [createRow, { isLoading: creatingRow }] = useCreateFactoryRowMutation()
  const [updateRow, { isLoading: updatingRow }] = useUpdateFactoryRowMutation()
  const [deleteRow, { isLoading: deletingRow }] = useDeleteFactoryRowMutation()
  const [copyReceive, { isLoading: copyingReceive }] = useCopyFactoryReceiveMutation()
  const [copyDelivery, { isLoading: copyingDelivery }] = useCopyFactoryDeliveryMutation()
  const [copyConsumables, { isLoading: copyingConsumables }] = useCopyFactoryConsumablesMutation()
  const [triggerReceiveQuality] = useLazyGetReceiveQualityQuery()
  const [saveReceiveQuality, { isLoading: savingQuality }] = useSaveReceiveQualityMutation()
  const [triggerDeliveryQuality] = useLazyGetDeliveryQualityQuery()
  const [saveDeliveryQuality, { isLoading: savingDeliveryQuality }] =
    useSaveDeliveryQualityMutation()
  // Per-user navigation prefs (lưu tab cuối user mở của từng factory).
  const { data: navPrefs } = useGetFactoryNavigationPrefsQuery()
  const [patchNavPrefs] = usePatchFactoryNavigationPrefsMutation()
  // Per-factory column visibility prefs (row riêng `factory_<code>` / `columns`).
  const { data: columnsPrefs } = useGetFactoryColumnsPrefsQuery(factoryCode, {
    skip: !factoryCode,
  })
  const [patchColumnsPrefs] = usePatchFactoryColumnsPrefsMutation()

  // Hydrate filter date trên load: ưu tiên giá trị đã lưu trong
  // user_preferences (per-factory), fallback BE context dateRange (legacy
  // shared filter_date), cuối cùng giữ localStorage default.
  useEffect(() => {
    if (serverDatesHydrated) return
    const savedPref = navPrefs?.filtersByFactory?.[factoryCode]
    if (savedPref?.startDate && savedPref?.endDate) {
      const next: FactoryDateRange = {
        startDate: savedPref.startDate,
        endDate: savedPref.endDate,
      }
      setFilters(next)
      setServerDatesHydrated(true)
      return
    }
    if (contextData?.dateRange) {
      const next: FactoryDateRange = {
        startDate: contextData.dateRange.startDate,
        endDate: contextData.dateRange.endDate,
      }
      setFilters(next)
      setServerDatesHydrated(true)
    }
  }, [contextData?.dateRange, navPrefs?.filtersByFactory, factoryCode, serverDatesHydrated])

  const listQuery = useGetFactoryTabRowsQuery(
    {
      factoryCode,
      tab: activeTab.endpoint,
      startDate: filters.startDate,
      endDate: filters.endDate,
      dataset: selectorValue,
    },
    {
      // Skip until server dates hydrated to avoid a wasted fetch with the
      // localStorage default before the user's saved range arrives. Skip
      // cũng cho `data/bom` + `data/bom-cost` vì page dedicated tự fetch
      // qua factoryBomApi / factoryCostApi.
      skip:
        activeTab.kind === 'summary' ||
        invalidRange ||
        !serverDatesHydrated ||
        inHub ||
        (activeTab.key === 'data' && (selectorValue === 'bom' || selectorValue === 'bom-cost')) ||
        ((activeTab.key === 'data' || activeTab.key === 'report') && !!selectorValue) ||
        !isFactoryTabBuilt(activeTab.key, selectorValue),
      refetchOnMountOrArgChange: true,
    },
  )
  // Cấp loading state cho nút Reload trên tab SUMMARY.
  // Dùng selector của RTK Query để đọc trực tiếp cache entry — không tạo subscription
  // mới ở parent (subscription kép từng làm nút Reload kẹt ở trạng thái fetching).
  const summarySelect = useMemo(
    () => factoryApi.endpoints.getFactorySummary.select(factoryCode),
    [factoryCode],
  )
  const summaryFetching = useAppSelector((s) => summarySelect(s).status === 'pending')
  const reloadBusy = activeTab.kind === 'summary' ? summaryFetching : listQuery.isFetching

  // Nonce arm overlay Loading cho BatchReportPage khi bấm Reload chung.
  const [batchReloadNonce, setBatchReloadNonce] = useState(0)
  // Khi user nhấn Reload trên tab SUMMARY, force refetch trực tiếp endpoint
  // summary thay vì dựa hoàn toàn vào tag invalidation — tag invalidation
  // phụ thuộc subscription timing, gọi `initiate(..., { forceRefetch: true })`
  // luôn ép một network request mới cho SUMMARY REPORT.
  const handleReload = useCallback(() => {
    // Batch Report (tab report + selector batch-report): invalidate broad → mọi
    // tab con của BatchReportPage refetch live (po-list, BOM detail/options/crop,
    // cost, qrefs, persist). Trước đây là nút Reload riêng trong BatchReportPage.
    if (activeTab.key === 'report' && selectorValue === 'batch-report') {
      dispatch(factoryApi.util.invalidateTags(['Factory', 'FactoryBom', 'FactoryCost']))
      // Arm lại overlay Loading của BatchReportPage (đổi nonce) — invalidate chỉ
      // refetch ngầm, không tự bật overlay (overlay chỉ arm khi tab|PO đổi).
      setBatchReloadNonce((n) => n + 1)
      return
    }
    if (activeTab.kind !== 'summary') return
    void dispatch(
      factoryApi.endpoints.getFactorySummary.initiate(factoryCode, {
        forceRefetch: true,
        subscribe: false,
      }),
    )
  }, [dispatch, factoryCode, activeTab.kind, activeTab.key, selectorValue])

  useEffect(() => {
    if (!location.pathname.startsWith(`/${factoryCode}`)) return

    const segments = location.pathname.split('/').filter(Boolean)
    const candidate = segments[1]
    if (isFactoryTabKey(candidate)) return
    // Không có sub-tab trên URL → restore tab cuối user đã mở cho factory này
    // (nếu navPrefs đã fetch xong). Fallback `summary` nếu chưa lưu.
    const saved = navPrefs?.tabsByFactory?.[factoryCode]
    const target = saved && isFactoryTabKey(saved) ? saved : 'summary'
    navigate(`/${factoryCode}/${target}`, { replace: true })
  }, [location.pathname, navigate, factoryCode, navPrefs])

  // Auto-save khi user đổi tab hoặc đổi factory: lưu lastFactory + tab hiện
  // tại cho factoryCode. Debounce 400ms để click nhanh không spam BE.
  useEffect(() => {
    if (!isFactoryTabKey(activeKey)) return
    const saved = navPrefs?.tabsByFactory?.[factoryCode]
    const lastSavedFactory = navPrefs?.lastFactory
    if (saved === activeKey && lastSavedFactory === factoryCode) return
    const t = window.setTimeout(() => {
      void patchNavPrefs({
        lastFactory: factoryCode,
        factoryCode,
        tab: activeKey,
      }).unwrap().catch(() => undefined)
    }, 400)
    return () => window.clearTimeout(t)
  }, [factoryCode, activeKey, navPrefs, patchNavPrefs])

  useEffect(() => {
    setSelectedRow(null)
  }, [activeKey, selectorValue])

  // Khi đổi factory: clear UI state thuộc factory cũ (selected row, pending
  // action, copy target). Re-hydrate server dates cho factory mới.
  useEffect(() => {
    setSelectedRow(null)
    setPendingAction(null)
    setCopyTarget(null)
    setExporting(null)
    setServerDatesHydrated(false)
  }, [factoryCode])

  useEffect(() => {
    if (!invalidRange) {
      void saveDateRange({ factoryCode, ...filters }).unwrap().catch(() => undefined)
      // Persist per-factory filter into user_preferences — chỉ patch nếu giá
      // trị khác snapshot trong cache, tránh PATCH thừa.
      const saved = navPrefs?.filtersByFactory?.[factoryCode]
      if (
        serverDatesHydrated &&
        (saved?.startDate !== filters.startDate || saved?.endDate !== filters.endDate)
      ) {
        void patchNavPrefs({
          factoryCode,
          startDate: filters.startDate,
          endDate: filters.endDate,
        }).unwrap().catch(() => undefined)
      }
    }
  }, [filters, invalidRange, saveDateRange, factoryCode, navPrefs, patchNavPrefs, serverDatesHydrated])

  const rows = useMemo(
    () => normalizeRows(listQuery.data?.items, activeTab.sampleRows),
    [activeTab.sampleRows, listQuery.data?.items],
  )

  const context = contextData ?? {
    title: 'BAO LOC FACTORY',
    storeChar: 'TLBL',
    factoryCode: 'baoloc',
    viewPrefix: 'tlc_factory_bl_',
    dateRange: filters,
    permissions: {
      canView: permission.canView,
      canEdit: permission.canEdit,
      canExport: permission.canView,
    },
  }

  const canEdit = permission.canEdit && context.permissions.canEdit

  // dataset export theo nhà máy đang xem; thiếu mapping → fallback Bảo Lộc.
  const exportDataset = FACTORY_EXPORT_DATASET[factoryCode] ?? 'data_bl'

  const downloadFactoryExcel = async (mode: 'all' | 'filter', url: string) => {
    if (!email) return

    setExporting(mode)
    try {
      const res = await fetch(url)
      if (!res.ok) throw new Error('Export failed')
      const blob = await res.blob()
      const href = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const filename = getDownloadFilename(res.headers.get('content-disposition'))
      const downloadName =
        mode === 'filter' && filename
          ? filename.replace(/\.xlsx$/i, '_filter.xlsx')
          : filename
      const fallback = `Factory_${exportDataset}${mode === 'filter' ? '_filter' : ''}.xlsx`
      a.href = href
      a.download = downloadName ?? fallback
      a.click()
      URL.revokeObjectURL(href)
    } finally {
      setExporting(null)
    }
  }

  const handleExportAll = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/factory/${exportDataset}?token=${encodeURIComponent(
      FACTORY_EXPORT_TOKEN,
    )}&email=${encodeURIComponent(email)}`
    await downloadFactoryExcel('all', url)
  }

  const handleExportFilter = async () => {
    const url = `${API_BASE_URL}/erp-legacy/download/factory_filter/${exportDataset}?token=${encodeURIComponent(
      FACTORY_EXPORT_TOKEN,
    )}&email=${encodeURIComponent(email)}&start_date=${encodeURIComponent(filters.startDate)}&end_date=${encodeURIComponent(filters.endDate)}`
    await downloadFactoryExcel('filter', url)
  }

  const handleExport = async (filtered: boolean) => {
    if (exporting !== null) return
    if (filtered) {
      await handleExportFilter()
    } else {
      await handleExportAll()
    }
  }

  const rowSaving = creatingRow || updatingRow
  const copyingBusy = copyingReceive || copyingDelivery || copyingConsumables

  const handleSaveNew = async (payload: FactoryBRowPayload) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    const tab = pendingAction?.tab ?? activeTab
    const ds = resolveDatasetForTab(tab, selectorValuesEffective)
    try {
      await createRow({ factoryCode, tab: tab.endpoint, dataset: ds, body: payload }).unwrap()
      snack.success('Record created')
      setPendingAction(null)
    } catch {
      snack.error('Could not create — check primary key and required columns')
    }
  }

  const handleSaveEdit = async (payload: FactoryBRowPayload) => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    const tab = pendingAction?.tab ?? activeTab
    const row = pendingAction?.row
    const ds = resolveDatasetForTab(tab, selectorValuesEffective)
    // Merge PK từ pendingAction.row vào body. Bắt buộc cho tab có serial PK
    // (issue/delivery/lot-allocation/data:*) — dialog không render id trong
    // form nên payload không có; BE `_update_row_impl` raise 400 nếu missing
    // PK. Spread sau payload để PK luôn authoritative (user không đổi PK).
    const pk = row ? getPkPayload(tab, row, ds) : {}
    const body = { ...payload, ...pk }
    try {
      await updateRow({ factoryCode, tab: tab.endpoint, dataset: ds, body }).unwrap()
      snack.success('Updated')
      // List refetch (invalidate) KHÔNG tự sync `selectedRow` (state parent giữ
      // object row cũ). Merge giá trị vừa lưu vào selectedRow để Edit lần sau
      // hydrate đúng dữ liệu mới, không lấy lại value dòng cũ.
      setSelectedRow((prev) => (prev ? ({ ...prev, ...body } as FactoryRow) : prev))
      setPendingAction(null)
    } catch {
      snack.error('Could not update')
    }
  }

  // PO List: áp đổi Finished↔Processing sau khi user confirm. Partial update —
  // chỉ gửi is_finished (+ po_clossing_date như form cũ: set hôm nay khi Finished,
  // clear khi Processing). BE freeze cột _edit khi → Finished (như Batch Report).
  const handleConfirmFinished = async (closingDate: string | null) => {
    if (!finishedConfirm) return
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      setFinishedConfirm(null)
      return
    }
    const { row, next } = finishedConfirm
    const ds = resolveDatasetForTab(activeTab, selectorValuesEffective)
    const pk = getPkPayload(activeTab, row, ds)
    const body = {
      ...pk,
      is_finished: next,
      // Closing Date do form Finished quyết định (default hôm nay, sửa được);
      // → Processing clear về null.
      po_clossing_date: next ? closingDate : null,
    } as unknown as FactoryBRowPayload
    setFinishedSaving(true)
    try {
      await updateRow({ factoryCode, tab: activeTab.endpoint, dataset: ds, body }).unwrap()
      snack.success(next ? 'PO marked as Finished' : 'PO reopened (Processing)')
      setFinishedConfirm(null)
      setSelectedRow((prev) => (prev ? { ...prev, is_finished: next } : prev))
    } catch {
      snack.error('Could not update PO status')
    } finally {
      setFinishedSaving(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (!canEdit) {
      snack.error('Read-only — you do not have edit permission for this factory.')
      return
    }
    if (pendingAction?.action !== 'delete' || !pendingAction.row) return
    const { tab, row } = pendingAction
    const ds = resolveDatasetForTab(tab, selectorValuesEffective)
    const pk = getPkPayload(tab, row, ds)
    if (!Object.keys(pk).length) {
      snack.error('Missing primary key for delete')
      setPendingAction(null)
      return
    }
    try {
      await deleteRow({ factoryCode, tab: tab.endpoint, dataset: ds, body: pk }).unwrap()
      snack.success('Successfully Deleted')
      setPendingAction(null)
      setSelectedRow(null)
    } catch {
      snack.error('Could not delete')
    }
  }

  const handleCopyConfirm = async (newId: string) => {
    if (!copyTarget) return
    try {
      if (copyTarget.kind === 'receive' && copyTarget.row.grn_id != null) {
        await copyReceive({
          factoryCode,
          fromGrnId: String(copyTarget.row.grn_id),
          toGrnId: newId,
        }).unwrap()
        snack.success('GRN copied')
      } else if (copyTarget.kind === 'delivery' && copyTarget.row.id != null) {
        const fromId = Number(copyTarget.row.id)
        if (Number.isNaN(fromId)) {
          snack.error('Invalid delivery line ID')
          return
        }
        await copyDelivery({ factoryCode, fromId, toGdnId: newId }).unwrap()
        snack.success('GDN copied')
      } else if (copyTarget.kind === 'consumables' && copyTarget.row.mrn_mdn_no != null) {
        await copyConsumables({
          factoryCode,
          fromMrnMdnNo: String(copyTarget.row.mrn_mdn_no),
          toMrnMdnNo: newId,
        }).unwrap()
        snack.success('MRN/MDN copied')
      }
      setCopyTarget(null)
    } catch {
      snack.error('Copy failed — check new code or duplicate key')
    }
  }

  const handleAction = (action: FactoryAction) => {
    if (
      action !== 'new' &&
      action !== 'copy' &&
      action !== 'print' &&
      action !== 'mass-upload' &&
      !selectedRow
    )
      return

    if (action === 'copy') {
      if (!selectedRow) return
      if (activeTab.endpoint === 'receive') {
        // Bám Access `btnCopy_Click` (form_frmFactoryReceiveSubForm.txt):
        // mở cùng form Add/Edit với fields cloned + grn_id reset (user nhập
        // mã mới) + id=0 → Save = INSERT. KHÔNG dùng CopyIdDialog (BE clone)
        // vì spec yêu cầu user xem/sửa fields trước khi commit.
        if (!isCrudEnabled(activeTab, selectorValue)) {
          snack.info('This dataset/tab is read-only — choose a writable dataset or another tab')
          return
        }
        setPendingAction({ action: 'copy', row: selectedRow, tab: activeTab })
        return
      }
      if (activeTab.endpoint === 'delivery') {
        // Bám Access `btnCopy_Click` (form_frmFactoryDeliverySubForm.txt): mở
        // cùng form AddStore với fields cloned từ dòng focus + gdn_id reset
        // (user nhập mã mới) + id=0 → Save = INSERT. Mirror Receive/Lot Mgt.
        if (!isCrudEnabled(activeTab, selectorValue)) {
          snack.info('This dataset/tab is read-only — choose a writable dataset or another tab')
          return
        }
        setPendingAction({ action: 'copy', row: selectedRow, tab: activeTab })
        return
      }
      if (activeTab.endpoint === 'consumables') {
        // Bám Access `btnCopy_Click` (form_frmFactoryConsumablesSubForm.txt):
        // mở form Add clone fields từ dòng focus, id=0 → Save = INSERT. Mirror
        // Receive/Delivery/Lot Mgt (user xem/sửa + re-suggest mã trước commit) —
        // KHÔNG dùng quick CopyIdDialog. Deviation: Access clear cả category
        // nhưng chị Đẹp yêu cầu copy đầy đủ → chỉ reset mrn_mdn_no (PK mới).
        if (!isCrudEnabled(activeTab, selectorValue)) {
          snack.info('This dataset/tab is read-only — choose a writable dataset or another tab')
          return
        }
        setPendingAction({ action: 'copy', row: selectedRow, tab: activeTab })
        return
      }
      if (activeTab.endpoint === 'lot-mgt') {
        // Bám Access `btnCopy_Click` (form_frmFactoryLotMgtSubForm.txt): mở
        // cùng form Add với fields cloned từ dòng focus + lot_id reset (user
        // nhập mã mới) + id=0 → Save = INSERT. KHÔNG dùng CopyIdDialog (BE clone)
        // — user cần xem/sửa fields trước khi commit, đúng spec.
        if (!isCrudEnabled(activeTab, selectorValue)) {
          snack.info('This dataset/tab is read-only — choose a writable dataset or another tab')
          return
        }
        setPendingAction({ action: 'copy', row: selectedRow, tab: activeTab })
        return
      }
      snack.info('This tab has no copy API yet')
      return
    }

    if (action === 'print') {
      if (!selectedRow) return
      if (activeTab.endpoint === 'receive' && selectedRow!.grn_id != null) {
        // Access port btnPrintRp_Click guards — extracted to helper để Save&Print
        // (FactoryReceiveQualityDialog btnSavePr) cũng dùng chung. Trước đó
        // Save&Print bypass precheck → cho printable cả khi qc_name/inspection
        // rỗng → bất nhất với Print button.
        const err = receivePrintPrecheck(selectedRow as Record<string, FactoryCell>)
        if (err) {
          snack.warning(err)
          return
        }
        setPrintReceiveGrnId(String(selectedRow!.grn_id))
        return
      }
      if (activeTab.endpoint === 'delivery' && selectedRow!.id != null) {
        // Access port btnPrintRp_Click → mở preview dialog (rpFactoryGdn).
        const idNum = Number(selectedRow!.id)
        if (Number.isNaN(idNum)) {
          snack.error('Missing valid GDN ID')
          return
        }
        const gdnIdRaw = selectedRow!.gdn_id
        setPrintDeliveryGdn(
          gdnIdRaw == null || String(gdnIdRaw).trim() === '' ? null : String(gdnIdRaw).trim()
        )
        setPrintDeliveryId(idNum)
        return
      }
      snack.info('Print is only for Receive (GRN) or Delivery (GDN)')
      return
    }

    if (action === 'quality') {
      if (!selectedRow) return
      // Delivery (GDN) Quality — mirror Receive flow, keyed bằng gdn_id.
      if (activeTab.endpoint === 'delivery') {
        if (deliveryQualityFetching) return
        const gdnIdRaw = selectedRow.gdn_id
        if (gdnIdRaw == null || String(gdnIdRaw).trim() === '') {
          snack.error('Selected row is missing gdn_id')
          return
        }
        const gdnId = String(gdnIdRaw).trim()
        void (async () => {
          setDeliveryQualityFetching(true)
          try {
            // Access `btnQC_Click` (frmFactoryDeliverySubForm): chỉ mở
            // `frmFactoryQryDeliveryAddQC` bind record đang chọn — KHÔNG confirm
            // Yes/No, KHÔNG view-only. Form luôn editable, prefill QC hiện có.
            const res = await triggerDeliveryQuality({ factoryCode, gdnId }, false).unwrap()
            const row = { ...(res as Record<string, FactoryCell>) }
            delete row._qc_exists
            setDeliveryQualityDialog({ mode: 'edit', gdnId, row })
          } catch {
            snack.error('Could not load QC data — check GDN')
          } finally {
            setDeliveryQualityFetching(false)
          }
        })()
        return
      }
      // Đang fetch QC payload trước đó — chặn double-click (cảm giác "lâu"
      // user dễ click 2 lần liên tiếp → bắn 2 request song song).
      if (qualityFetching) return
      // Bám flow Access `btnQC_Click` (form_frmFactoryReceiveSubForm.txt):
      //   1. GET QC payload cho GRN.
      //   2. Nếu BE trả `_qc_exists` = true → confirm Yes/No.
      //   3. Yes → mở dialog mode 'edit' (re-enter); No → mở mode 'view'.
      //   4. Nếu chưa có QC → mở dialog mode 'new'.
      if (activeTab.endpoint !== 'receive') {
        snack.info('Quality is only for Receive (GRN)')
        return
      }
      const grnIdRaw = selectedRow.grn_id
      if (grnIdRaw == null || String(grnIdRaw).trim() === '') {
        snack.error('Selected row is missing grn_id')
        return
      }
      const grnId = String(grnIdRaw).trim()
      void (async () => {
        setQualityFetching(true)
        try {
          // preferCacheValue=false → force fresh fetch. Tránh case: lần đầu
          // click Quality lúc chưa có QC → `_qc_exists=false` cache; user
          // save QC → click Quality lần 2 với cùng args → RTK lazy trả
          // cache cũ → FE skip confirm dialog (bug GRN-TLBL-252745).
          const res = await triggerReceiveQuality({ factoryCode, grnId }, false).unwrap()
          const row = { ...(res as Record<string, FactoryCell>) }
          delete row._qc_exists
          if (res._qc_exists) {
            setQualityConfirm({ grnId, row })
          } else {
            setQualityDialog({ mode: 'new', grnId, row })
          }
        } catch {
          snack.error('Could not load QC data — check GRN')
        } finally {
          setQualityFetching(false)
        }
      })()
      return
    }

    if (!isCrudEnabled(activeTab, selectorValue)) {
      snack.info('This dataset/tab is read-only — choose a writable dataset or another tab')
      return
    }

    if (action === 'mass-upload') {
      // Hiện chỉ Lot Allocation có Mass upload (tham khảo /futures/orders).
      if (activeTab.endpoint === 'lot-allocation') setMassUploadOpen(true)
      else snack.info('Mass upload is not available for this tab')
      return
    }
    if (action === 'finished') {
      if (!selectedRow) return
      // Đổi Processing↔Finished bằng nút riêng + confirm (KHÔNG còn trong form).
      setFinishedConfirm({ row: selectedRow, next: !isRowFinished(selectedRow) })
      return
    }
    if (action === 'new') {
      setPendingAction({ action: 'new', row: null, tab: activeTab })
      return
    }
    // PO List: row Finished khoá Edit + Delete (chỉ đổi trạng thái qua nút
    // Finished/Processing). Guard ở handler để double-click/Enter cũng chặn.
    if (
      (action === 'edit' || action === 'delete') &&
      activeTab.key === 'po-list' &&
      selectedRow &&
      isRowFinished(selectedRow)
    ) {
      snack.info('This PO is finished — set it back to Processing to edit or delete.')
      return
    }
    if (action === 'edit' && selectedRow) {
      setPendingAction({ action: 'edit', row: selectedRow, tab: activeTab })
      return
    }
    if (action === 'delete' && selectedRow) {
      setPendingAction({ action: 'delete', row: selectedRow, tab: activeTab })
    }
  }

  const crudUiEnabled = isCrudEnabled(activeTab, selectorValue)

  if (!permission.canView) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={800}>
          No access
        </Typography>
      </Box>
    )
  }

  // Switching factory (URL param changed) — chờ context của factory mới về
  // trước khi render header/tabs/summary để tránh nhấp nháy data factory cũ.
  if (switchingFactory) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 1.5, minHeight: 240, p: 4 }}>
        <CircularProgress size={32} />
        <Typography variant="body2" color="text.secondary">
          Loading factory data…
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <FactoryHeader
        title={context.title}
        storeChar={context.storeChar}
        filters={filters}
        invalidRange={invalidRange}
        exporting={exporting}
        email={email}
        reloadBusy={reloadBusy}
        onFilterChange={setFilters}
        onExport={(filtered) => void handleExport(filtered)}
        onReload={handleReload}
      />

      {invalidRange ? (
        <Typography variant="caption" color="error" sx={{ alignSelf: 'flex-end', mb: 1 }}>
          Start date must be before or equal to end date
        </Typography>
      ) : null}

      <Card sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        {/* Block Tabs chung + slot phải cho Zoom control (chỉ tab Summary). */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
        <Tabs
          value={activeTab.hiddenFromTopTabs ? 'data' : activeKey}
          onChange={(_, value: string) => {
            // Click vào tab hub (`data` / `report`) trên top bar → navigate
            // về URL hub (không dataset). Selector của hub được derive từ URL
            // segment thứ 3, nên về `/factory/<code>/<hub>` tự động show hub.
            navigate(`/${factoryCode}/${value}`, { replace: false })
          }}
          variant="scrollable"
          scrollButtons="auto"
          sx={{
            minHeight: 34,
            '& .MuiTabs-indicator': {
              height: 2.5,
              borderRadius: '3px 3px 0 0',
            },
            '& .MuiTab-root': {
              position: 'relative',
              minHeight: 34,
              minWidth: 0,
              py: 0.25,
              px: 1.5,
              fontSize: '0.78rem',
              fontWeight: 600,
              letterSpacing: 0.15,
              textTransform: 'none',
              color: 'text.secondary',
              borderRadius: '6px 6px 0 0',
              transition: 'color 140ms ease, background-color 140ms ease',
              '&:hover': {
                color: 'text.primary',
                bgcolor: 'action.hover',
              },
              '&.Mui-selected': {
                color: 'primary.main',
                fontWeight: 700,
              },
              // vạch ngăn dọc ngắn, căn giữa, mảnh giữa các tab — ẩn cạnh tab đang chọn/hover cho sạch
              '&:not(:last-of-type)::after': {
                content: '""',
                position: 'absolute',
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: 16,
                width: '1px',
                bgcolor: 'divider',
                opacity: 0.6,
                transition: 'opacity 140ms ease',
              },
              '&:hover::after, &.Mui-selected::after': { opacity: 0 },
            },
          }}
        >
          {FACTORY_TABS.filter((tab) => !tab.hiddenFromTopTabs).map((tab) => (
            <Tab
              key={tab.key}
              label={tab.label}
              value={tab.key}
              onClick={
                tab.key === 'data' || tab.key === 'report'
                  ? () => {
                      // Tabs.onChange chỉ fire khi value đổi. Khi user đang ở
                      // /<hub>/<dataset> hoặc 1 hidden tab (Tabs.value đã force
                      // về 'data'), click hub tab cần navigate về URL hub mà
                      // onChange sẽ không trigger. onClick force navigate luôn —
                      // navigate idempotent với URL hiện tại nên không lặp.
                      if (
                        location.pathname !== `/${factoryCode}/${tab.key}`
                      ) {
                        navigate(`/${factoryCode}/${tab.key}`)
                      }
                    }
                  : undefined
              }
            />
          ))}
        </Tabs>
        </Box>
        {activeTab.kind === 'summary' ? (
          <Box sx={{ flexShrink: 0, px: 1, pb: 0.5, alignSelf: 'center' }}>{summaryZoomControl}</Box>
        ) : null}
        </Box>

        <CardContent
          sx={{
            ...CARD_CONTENT_COMPACT,
            gap: 1.25,
            // Batch Report có khung viền riêng → giảm padding CardContent để
            // khung report sát Card chung, bớt margin thừa (chỉ scope report).
            ...(activeTab.key === 'report' && selectorValue === 'batch-report'
              ? { p: 0.25, '&:last-child': { pb: 0.25 } }
              : {}),
          }}
        >
          {activeTab.kind === 'summary' ? (
            <FactorySummary factoryCode={factoryCode} factoryName={contextData?.title} zoom={summaryZoom} />
          ) : !isFactoryTabBuilt(activeTab.key, selectorValue) ? (
            <ComingSoonPage
              title={activeTab.label}
              embedded
              status={getFactoryTabUnbuild(activeTab.key, selectorValue)?.status}
            />
          ) : inHub ? (
            <FactoryHub
              tab={activeTab}
              layout={inDataHub ? FACTORY_DATA_HUB_LAYOUT : FACTORY_REPORT_HUB_LAYOUT}
              onSelect={(value) => navigate(`/${factoryCode}/${activeTab.key}/${value}`)}
              onNavigate={(tabKey) => navigate(`/${factoryCode}/${tabKey}`)}
            />
          ) : activeTab.key === 'data' && selectorValue === 'bom' ? (
            <FactoryBomPage
              factoryCode={factoryCode}
              canEdit={canEdit}
              onBackToHub={() => navigate(`/${factoryCode}/data`)}
            />
          ) : activeTab.key === 'data' && selectorValue === 'bom-cost' ? (
            <FactoryCostPage
              factoryCode={factoryCode}
              canEdit={canEdit}
              onBackToHub={() => navigate(`/${factoryCode}/data`)}
            />
          ) : activeTab.key === 'data' && selectorValue === 'item-list' ? (
            // Card Item List → bảng list_and_valo (tái dùng References ListAndValo
            // của Purchase Contract; master data dùng chung, read-only).
            <FactoryItemListView onBackToHub={() => navigate(`/${factoryCode}/data`)} />
          ) : activeTab.key === 'data' && selectorValue === 'matching-scontract' ? (
            // Nhúng trang Matching · S Contract NGAY trong workspace Factory
            // (yêu cầu chị Đẹp): acc Factory xem được mà không phải điều hướng
            // ra route /matching/s-contract (route đó bị giới hạn quyền). Data
            // lấy từ API matching-ps (chỉ cần auth), không qua route guard.
            // Bố cục giống /baoloc/deduction-rate (header tích hợp) — xem
            // SContractTab khi nhận `onBackToHub`.
            <SContractTab onBackToHub={() => navigate(`/${factoryCode}/data`)} />
          ) : activeTab.key === 'data' && selectorValue === 'matching-pcontract' ? (
            // Nhúng trang Matching · P Contract trong workspace Factory (như
            // S Contract) — bố cục giống /baoloc/deduction-rate.
            <PContractTab onBackToHub={() => navigate(`/${factoryCode}/data`)} />
          ) : activeTab.key === 'quality-standard' ? (
            <FactoryQualityStandardPage
              factoryCode={factoryCode}
              canEdit={canEdit}
              onBackToHub={() => navigate(`/${factoryCode}/data`)}
            />
          ) : activeTab.key === 'deduction-rate' ? (
            <FactoryDeductionRatePage
              factoryCode={factoryCode}
              canEdit={canEdit}
              onBackToHub={() => navigate(`/${factoryCode}/data`)}
            />
          ) : activeTab.key === 'report' && selectorValue === 'batch-report' ? (
            <BatchReportPage
              factoryCode={factoryCode}
              startDate={filters.startDate}
              endDate={filters.endDate}
              canEdit={canEdit}
              reloadSignal={batchReloadNonce}
              onBackToHub={() => navigate(`/${factoryCode}/report`)}
            />
          ) : (activeTab.key === 'data' || activeTab.key === 'report') && selectorValue ? (
            (() => {
              // Card landing trên ComingSoonPage — wrap với header strip
              // (Back + accent pill) để user nhận diện ngay card đã chọn,
              // đồng bộ với FactoryBomPage/FactoryCostPage/BatchReportPage.
              const selector = activeTab.selectors?.find((s) => s.value === selectorValue)
              const label = selector?.label ?? activeTab.label
              const meta =
                FACTORY_DATA_HUB_META[selectorValue] ?? FACTORY_DATA_HUB_FALLBACK
              const AccentIcon = meta.Icon as React.ComponentType<{
                sx?: SxProps<Theme>
                fontSize?: 'small' | 'medium'
              }>
              return (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 1 }}>
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    useFlexGap
                    flexWrap="wrap"
                    sx={{ mb: 0.5 }}
                  >
                    <Button
                      size="small"
                      variant="outlined"
                      color="inherit"
                      startIcon={<ArrowBackIcon sx={{ fontSize: 14 }} />}
                      onClick={() => navigate(`/${factoryCode}/${activeTab.key}`)}
                      sx={{
                        textTransform: 'none',
                        fontSize: '0.72rem',
                        py: 0.25,
                        px: 1,
                        minHeight: 28,
                      }}
                    >
                      {activeTab.key === 'data' ? 'Datasets' : 'Reports'}
                    </Button>
                    <Stack
                      direction="row"
                      spacing={0.625}
                      alignItems="center"
                      sx={{
                        ml: 0.5,
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        backgroundColor: meta.color,
                        color: '#fff',
                        boxShadow: `0 1px 3px ${alpha(meta.color, 0.45)}`,
                      }}
                    >
                      <AccentIcon sx={{ fontSize: '0.95rem', color: '#fff' }} />
                      <Typography
                        variant="subtitle2"
                        sx={{
                          fontWeight: 800,
                          fontSize: '0.85rem',
                          color: '#fff',
                          textTransform: 'uppercase',
                          letterSpacing: 0.6,
                          whiteSpace: 'nowrap',
                          lineHeight: 1.1,
                        }}
                      >
                        {label}
                      </Typography>
                    </Stack>
                  </Stack>
                  <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                    <ComingSoonPage title={label} embedded />
                  </Box>
                </Box>
              )
            })()
          ) : !serverDatesHydrated ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
              <CircularProgress size={20} />
            </Box>
          ) : (
            <FactoryListWorkspace
              tab={activeTab}
              rows={rows}
              loading={listQuery.isFetching}
              selectedRow={selectedRow}
              selectorValue={selectorValue}
              canEdit={canEdit}
              crudEnabled={crudUiEnabled}
              onSelectorChange={(value) => {
                if (isHubTab) {
                  navigate(`/${factoryCode}/${activeTab.key}/${value}`)
                } else {
                  setSelectorValues((prev) => ({ ...prev, [activeTab.key]: value }))
                }
              }}
              // Toggle: click row đang selected → bỏ select (child gọi next=row,
              // mình so id để no-op nếu giống prev). Convention §6.5 table-style.
              onSelectedRowChange={(next) =>
                setSelectedRow((prev) =>
                  prev != null && next != null && prev.id === next.id ? null : next,
                )
              }
              onAction={handleAction}
              onBackToHub={
                activeTab.key === 'data' || activeTab.key === 'report'
                  ? () => navigate(`/${factoryCode}/${activeTab.key}`)
                  : activeTab.hiddenFromTopTabs
                    ? () => navigate(`/${factoryCode}/data`)
                    : undefined
              }
              savedColumnsByKey={columnsPrefs?.columns}
              defaultColumnsByKey={columnsPrefs?.defaults}
              onColumnsPersist={(tabKey, cols, defaults) => {
                void patchColumnsPrefs({ factoryCode, tabKey, columns: cols, defaults })
                  .unwrap()
                  .catch(() => undefined)
              }}
            />
          )}
        </CardContent>
      </Card>

      {/* Stack / Receive dùng form dialog dedicated bám Access spec
       *  (`frmFactoryQryStackAdd` / `frmFactoryQryReceiveAddStore`).
       *  Tab khác fallback ActionDialog generic. */}
      {pendingAction &&
      pendingAction.tab.key === 'stack' &&
      (pendingAction.action === 'new' || pendingAction.action === 'edit') ? (
        <StackFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          existingRows={rows}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            if (pendingAction.action === 'new') void handleSaveNew(payload)
            else void handleSaveEdit(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'receive' &&
        (pendingAction.action === 'new' ||
          pendingAction.action === 'edit' ||
          pendingAction.action === 'copy') ? (
        <ReceiveFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          existingRows={rows}
          factoryCode={factoryCode}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            // Copy = INSERT mới (bám Access `btnCopy_Click` set id=0).
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'delivery' &&
        (pendingAction.action === 'new' ||
          pendingAction.action === 'edit' ||
          pendingAction.action === 'copy') ? (
        <DeliveryFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          existingRows={rows}
          factoryCode={factoryCode}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            // Copy = INSERT mới (bám Access `btnCopy_Click` set id=0).
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'consumables' &&
        (pendingAction.action === 'new' ||
          pendingAction.action === 'edit' ||
          pendingAction.action === 'copy') ? (
        <ConsumablesFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          existingRows={rows}
          factoryCode={factoryCode}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            // Copy = INSERT mới (bám Access `btnCopy_Click`: id=0 + mrn_mdn_no
            // cleared). Edit = UPDATE; parent merge PK mrn_mdn_no từ row.
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'issue' &&
        (pendingAction.action === 'new' || pendingAction.action === 'edit') ? (
        <IssueFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'lot-mgt' &&
        (pendingAction.action === 'new' ||
          pendingAction.action === 'edit' ||
          pendingAction.action === 'copy') ? (
        <LotMgtFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            // Copy = INSERT mới (bám Access `btnCopy_Click` set id=0).
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'po-list' &&
        (pendingAction.action === 'new' || pendingAction.action === 'edit') ? (
        <PoListFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          existingRows={rows}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'internalmoving' &&
        selectorValue === 'internal-moving' &&
        (pendingAction.action === 'new' || pendingAction.action === 'edit') ? (
        <InternalMovingFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : pendingAction &&
        pendingAction.tab.key === 'lot-allocation' &&
        (pendingAction.action === 'new' || pendingAction.action === 'edit') ? (
        <LotAllocationFormDialog
          open
          mode={pendingAction.action}
          row={pendingAction.row}
          saving={rowSaving}
          factoryCode={factoryCode}
          canEdit={canEdit}
          onClose={() => setPendingAction(null)}
          onSave={(payload) => {
            if (pendingAction.action === 'edit') void handleSaveEdit(payload)
            else void handleSaveNew(payload)
          }}
        />
      ) : (
        <ActionDialog
          pendingAction={pendingAction?.action === 'delete' ? null : pendingAction}
          saving={rowSaving}
          onClose={() => setPendingAction(null)}
          onSaveNew={(payload) => void handleSaveNew(payload)}
          onSaveEdit={(payload) => void handleSaveEdit(payload)}
        />
      )}
      <LotAllocationMassUploadDialog
        open={massUploadOpen}
        factoryCode={factoryCode}
        onClose={() => setMassUploadOpen(false)}
      />
      <CopyIdDialog
        open={!!copyTarget}
        kind={copyTarget?.kind ?? null}
        busy={copyingBusy}
        fromLabel={
          copyTarget
            ? copyTarget.kind === 'receive'
              ? String(copyTarget.row.grn_id ?? '')
              : copyTarget.kind === 'delivery'
                ? String(copyTarget.row.gdn_id ?? copyTarget.row.id ?? '')
                : String(copyTarget.row.mrn_mdn_no ?? '')
            : ''
        }
        onClose={() => setCopyTarget(null)}
        onConfirm={(id) => void handleCopyConfirm(id)}
      />
      <ConfirmDialog
        open={pendingAction?.action === 'delete'}
        title={`Delete ${pendingAction?.tab.label ?? activeTab.label}`}
        message={
          pendingAction?.action === 'delete' && pendingAction.row
            ? pendingAction.tab.key === 'stack'
              ? `Delete stack "${String(pendingAction.row.stack_id ?? pendingAction.row.id)}"?`
              : pendingAction.tab.key === 'receive'
                ? `Do you want to delete = '${String(pendingAction.row.grn_id ?? pendingAction.row.id)}'?`
                : pendingAction.tab.key === 'delivery'
                  ? `Do you want to delete = '${String(pendingAction.row.gdn_id ?? pendingAction.row.id)}'?`
                  : pendingAction.tab.key === 'issue'
                  ? (
                      <>
                        Delete this issue record?
                        <br />
                        Stack ID: <b>{String(pendingAction.row.stack_id ?? '')}</b>
                        <br />
                        PO No: <b>{String(pendingAction.row.po_no ?? '')}</b>
                      </>
                    )
                  : pendingAction.tab.key === 'lot-mgt'
                    ? `Do you want to delete lot "${String(pendingAction.row.lot_id ?? pendingAction.row.id)}"?`
                    : pendingAction.tab.key === 'lot-allocation'
                      ? (
                          <>
                            Delete this lot allocation?
                            <br />
                            Lot ID: <b>{String(pendingAction.row.lot_id ?? '')}</b>
                            <br />
                            GRN ID: <b>{String(pendingAction.row.grn_id ?? '')}</b>
                          </>
                        )
                      : pendingAction.tab.key === 'po-list'
                        ? `Do you want to delete PO "${String(pendingAction.row.po_no ?? pendingAction.row.id)}"?`
                        : `Delete the selected record "${String(pendingAction.row.id)}"? The record will be hidden from the list. Contact an administrator to restore it.`
            : 'Delete the selected record?'
        }
        destructive
        confirmText="Delete"
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setPendingAction(null)}
        loading={deletingRow}
      />

      {/* PO List — đổi Processing↔Finished. → Finished mở form Closing Date
          (default hôm nay, sửa được) rồi mới Save; → Processing chỉ confirm. */}
      <PoFinishedDialog
        open={!!finishedConfirm}
        toFinished={!!finishedConfirm?.next}
        poNo={String(finishedConfirm?.row.po_no ?? '')}
        saving={finishedSaving}
        onClose={() => !finishedSaving && setFinishedConfirm(null)}
        onConfirm={(closingDate) => void handleConfirmFinished(closingDate)}
      />

      {/* Quality flow — loading overlay khi đang fetch QC payload. Trước đó
       *  click button không thấy phản hồi tới khi dialog hiện ra (cảm giác
       *  "lâu") vì state `qualityFetching` chỉ track mà không render UI. */}
      {(qualityFetching || deliveryQualityFetching) && (
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            zIndex: 1300,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'rgba(0,0,0,0.25)',
          }}
        >
          <CircularProgress size={40} />
        </Box>
      )}
      {/* Quality flow — confirm Yes/No theo Access btnQC_Click khi đã có QC. */}
      <ConfirmDialog
        open={qualityConfirm !== null}
        title="Receive - Quality Deduction"
        message={
          qualityConfirm
            ? `A quality deduction already exists for '${qualityConfirm.grnId}'.\nDo you want to enter a new value?`
            : ''
        }
        confirmLabel="Yes (Re-enter)"
        cancelLabel="No (View only)"
        onConfirm={() => {
          if (!qualityConfirm) return
          setQualityDialog({ mode: 'edit', grnId: qualityConfirm.grnId, row: qualityConfirm.row })
          setQualityConfirm(null)
        }}
        onCancel={() => {
          if (!qualityConfirm) return
          setQualityDialog({ mode: 'view', grnId: qualityConfirm.grnId, row: qualityConfirm.row })
          setQualityConfirm(null)
        }}
      />

      <FactoryReceiveQualityDialog
        open={qualityDialog !== null}
        mode={qualityDialog?.mode ?? 'new'}
        factoryCode={factoryCode}
        initialData={qualityDialog?.row ?? null}
        saving={savingQuality}
        onClose={() => setQualityDialog(null)}
        onSave={async (payload) => {
          if (!qualityDialog) return
          try {
            await saveReceiveQuality({
              factoryCode,
              grnId: qualityDialog.grnId,
              body: payload,
            }).unwrap()
            snack.success('Successfully Saved')
            setQualityDialog(null)
            setSelectedRow(null)
          } catch {
            snack.error('Could not save Quality')
          }
        }}
        onSaveAndPrint={async (payload) => {
          // Access btnSavePr_Click (form_frmFactoryQryReceiveAddQC.txt:11655):
          // call btnSave_Click rồi OpenReport rpFactoryGrn. OpenReport ở
          // Access cũng đi qua btnPrintRp_Click guard → mirror precheck ở đây
          // để không vào step "mở form" khi QC chưa đủ. Dùng `payload` để
          // ưu tiên giá trị user vừa nhập (chưa kịp refetch grid).
          if (!qualityDialog) return
          const grnId = qualityDialog.grnId
          const rowSnap = qualityDialog.row
          try {
            await saveReceiveQuality({
              factoryCode,
              grnId,
              body: payload,
            }).unwrap()
            snack.success('Successfully Saved')
            setQualityDialog(null)
            setSelectedRow(null)
            const precheckErr = receivePrintPrecheck(rowSnap, payload)
            if (precheckErr) {
              snack.warning(precheckErr)
              return
            }
            setPrintReceiveGrnId(grnId)
          } catch {
            snack.error('Could not save Quality')
          }
        }}
      />
      <ReceivePrintDialog
        open={printReceiveGrnId !== null}
        factoryCode={factoryCode}
        grnId={printReceiveGrnId}
        canEdit={canEdit}
        onClose={() => setPrintReceiveGrnId(null)}
      />

      {/* Delivery (GDN) Quality — Access btnQC mở thẳng form editable, không
          confirm/view-only. */}
      <DeliveryQualityDialog
        open={deliveryQualityDialog !== null}
        mode={deliveryQualityDialog?.mode ?? 'new'}
        factoryCode={factoryCode}
        initialData={deliveryQualityDialog?.row ?? null}
        saving={savingDeliveryQuality}
        onClose={() => setDeliveryQualityDialog(null)}
        onSave={async (payload) => {
          if (!deliveryQualityDialog) return
          try {
            await saveDeliveryQuality({
              factoryCode,
              gdnId: deliveryQualityDialog.gdnId,
              body: payload,
            }).unwrap()
            snack.success('Successfully Saved')
            setDeliveryQualityDialog(null)
            setSelectedRow(null)
          } catch {
            snack.error('Could not save Quality')
          }
        }}
      />
      <DeliveryPrintDialog
        open={printDeliveryId !== null}
        factoryCode={factoryCode}
        deliveryId={printDeliveryId}
        gdnId={printDeliveryGdn}
        canEdit={canEdit}
        onClose={() => {
          setPrintDeliveryId(null)
          setPrintDeliveryGdn(null)
        }}
      />
    </Box>
  )
}
